// Hamburg, "Harbour lights on the Elbe". A blustery late-autumn night on a HafenCity quay: the
// Elbphilharmonie's wavy glass crown on its red-brick warehouse, Speicherstadt gables and copper turrets
// along a Fleet with an iron footbridge, the Michel's spire at the end of the canal, container cranes
// across the Elbe, a huge container ship gliding past, a harbour ferry, a Barkasse, gulls, a seal,
// a cat on a bollard and hard, gusty rain slanting in on the wind.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll,
  hash, rng, bayer, hex, makeCanvas, px, rect, makeHalo, drawHalo, reflect, streak, text, textWidth, makePitch, hands } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const HOR = 160;                                     // far bank of the Elbe: the container terminal quay
const SHIP_WL = 185;                                 // waterline of the passing container ship
const EP = { x0: 164, x1: 282, end: 14, glass: 137, top: 143, base: 184, wl: 190 };   // Elbphilharmonie
const PZ = { y: EP.glass + 1, floor: EP.glass + 4 };   // the Plaza: its soffit row and the row its people stand on
const EP_CX = 223;                                   // focusX: the whole building fits the portrait crop
const FB = { x0: 288, x1: 344, base: 196 };          // far Speicherstadt block
const NB = { x0: 392, x1: 480, base: 204 };          // near Speicherstadt block (runs off the right edge)
const BRG = { x0: 338, x1: 397, deck: 181 };         // iron footbridge over the mouth of the Fleet
const MI = { cx: 368, tip: 62, clock: 102 };         // St. Michaelis, seen down the Fleet
const BB = { x: 294, y: 130, w: 44, h: 18 };         // the billboard's face, hung on the far warehouse under its cornice
const BB_LAMPS = [BB.x + 6, BB.x + 22, BB.x + 38];   // three floodlamps under its catwalk, shining up at the face
const BB_LAMP_Y = BB.y + BB.h + 7;                   // their lit lenses (y 155)
const QUAY = 240;                                    // our quay edge
const MOON = { x: 92, y: 30 };
const LAMP = { x: 34, arm: 52, top: 112 };           // quay lamp post with a hanging lantern
const BOLLARDS = [132, 262, 424];
const CAT = { x: 262 };                              // the cat sits on the middle bollard

/* ================= Colour + pixel helpers ================= */
const RGB = {};
const rgbOf = h => RGB[h] || (RGB[h] = hex(h));
/** ordered-dither pick from a palette (v in 0..1) */
function dpick(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** ImageData-backed painter for prerendering (hex colours) */
function painter(w = W, h = H) {
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  const p = { c, x, w, h };
  p.set = (i, y, col) => {
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
  };
  p.has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  p.rect = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) p.set(i, y, col); };
  p.line = (x0, y0, x1, y1, col) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), col);
  };
  p.done = () => { x.putImageData(img, 0, 0); return c; };
  return p;
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function flipCanvas(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
/** per-frame pixel line (for booms, ropes, stays) */
function pline(x0, y0, x1, y1, col) {
  ctx.fillStyle = col;
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), 1, 1);
}

/* ================= Wind: one gust curve drives rain slant, flags, lamps, gulls and rain sheets ================= */
const gust = t => clamp(0.44 + 0.3 * wave(t, 5, 0.12) + 0.18 * wave(t, 13, 0.47) + 0.1 * wave(t, 29, 0.8), 0, 1);

/* ================= Sky: cold steel-blue storm sky, sodium glow over the port ================= */
const SKY = ['#060b12', '#080f18', '#0b131e', '#0e1824', '#121d2b', '#162332', '#1b2a39', '#213140', '#283947',
  '#30414d', '#3a4a53', '#465357', '#545d5a', '#635f58', '#72675a'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 168), 1.35) * 0.72;
    v += Math.max(0, 1 - Math.hypot((i - 70) / 250, (y - 170) / 82)) * 0.32;        // the terminal's floodlights
    v += Math.max(0, 1 - Math.hypot((i - 430) / 170, (y - 150) / 95)) * 0.12;       // floodlit Speicherstadt
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 95, (y - MOON.y) / 60)) * 0.16;  // moonlit cloud deck
    const f = clamp(v, 0, 1) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const STARS = [];
{
  const r = rng(12);
  while (STARS.length < 26) {
    const x = Math.floor(r() * W), y = 4 + Math.floor(r() * 70);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
    STARS.push({ x, y, k: 17 + Math.floor(r() * 60), ph: r(), b: 0.25 + r() * 0.5 });
  }
}
/** waning gibbous moon, dimmed by the rain haze */
function renderMoon() {
  const R = 6, s = R * 2 + 1, p = painter(s, s);
  const craters = [[-2, -1, 1.3], [1, 2, 1.1], [2, -2, 0.9]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - R, dy = y - R, dd = Math.hypot(dx, dy);
    if (dd > R + 0.3) continue;
    if (Math.hypot(dx + 8.6, dy - 1) < R + 1.2) continue;                  // the dark limb (waning)
    let col = dx + dy < -3 ? '#f0f4f4' : '#d6dee2';
    if (dd > R - 1) col = dx + dy > 0 ? '#9aa8b2' : '#c0cad0';
    for (const [cx, cy, cr] of craters) if (Math.hypot(dx - cx, dy - cy) < cr) col = '#aab6be';
    p.set(i, y, col);
  }
  return p.done();
}

/* ================= Wind-torn clouds (tileable, fast scud; moonlit tops, port-lit bellies) ================= */
const CLOUD_DEFS = [
  { seed: 13, y: -8, h: 58, banks: 5, k: 3, pal: { top: '#26364a', body: '#141e29', core: '#101922', bot: '#2c3a46', bot2: '#1c2833' } },
  { seed: 27, y: 26, h: 56, banks: 6, k: 5, pal: { top: '#3a4e60', body: '#1c2a36', core: '#17232e', bot: '#44505a', bot2: '#2c3a46' } },
  { seed: 41, y: 58, h: 50, banks: 6, k: 8, pal: { top: '#506270', body: '#2c3a44', core: '#26333c', bot: '#6a645c', bot2: '#46494a' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 70 + r() * 110, base = h * (0.45 + r() * 0.3);
    const np = 8 + Math.floor(r() * 7);
    for (let i = 0; i < np; i++) {
      const rx = 13 + r() * 26, ry = 2.5 + r() * 4.5;                   // flat, stretched: wind-torn scud
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.5, rx, ry, base: base + ry * 0.2 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.4;
      if (v > best) best = v;
    }
    // ragged, streaky edges combed out by the wind
    best += 0.2 * Math.sin(TAU * 13 * x / W + y * 0.9 + def.seed) * Math.sin(TAU * 7 * x / W - y * 0.3)
      + 0.1 * Math.sin(TAU * 31 * x / W + y * 1.7);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k };
}

/* ================= Container terminal across the Elbe: stacks, a moored feeder, gantry cranes ================= */
const RAIL = HOR - 4;                                // crane rails on the terminal quay
const CB = HOR - 36;                                 // boom top chord
const APEX = HOR - 56;                               // A-frame apex
const BOOM_LEN = 30;
const CRANES = [
  { c: 24, type: 'flat', k: 3, ph: 0.1, kh: 7, ph2: 0.3, lit: 0 },
  { c: 68, type: 'up', lit: 0.4 },
  { c: 110, type: 'flat', k: 4, ph: 0.55, kh: 9, ph2: 0.7, lit: 0.8 },
  { c: 150, type: 'move', lit: 1.3 },                // its boom is raised and lowered once per loop
  { c: 300, type: 'up', lit: 1.7 },                  // mostly hidden behind the Speicherstadt
];
const CR = { hi: '#7a8ea2', mid: '#46566a', dk: '#2c3846', lit: '#c08a5a', litD: '#6a503a', house: '#34424f' };
const UP_TH = 1.32;
const upTip = (c, th) => [Math.round(c - BOOM_LEN * Math.cos(th)), Math.round(CB + 1 - BOOM_LEN * Math.sin(th))];
const moveTh = t => 0.66 * (1 - Math.cos(TAU * t / LOOP));
const MASTS = [10, 58, 98, 140];                     // high-mast floodlights over the stacks
const CONT = [['#6a3430', '#86463c'], ['#2c4460', '#3e5a78'], ['#355236', '#48684a'], ['#7a6030', '#967a44'],
  ['#4c4c56', '#62626c'], ['#7a4428', '#96583a'], ['#5a2a3a', '#743c4c'], ['#3a5a6a', '#4e7282']];

function craneStatic(p, cr, r) {
  const c = cr.c, g = RAIL;
  for (let y = g - 26; y < g; y++) {                                  // portal legs, floodlit at the foot
    const low = y > g - 10;
    p.set(c, y, low ? CR.lit : CR.hi); p.set(c + 1, y, low ? CR.litD : CR.mid);
    p.set(c + 16, y, low ? CR.lit : CR.mid); p.set(c + 17, y, low ? CR.litD : CR.dk);
  }
  p.rect(c - 1, g - 1, 4, 1, '#161c24'); p.rect(c + 15, g - 1, 4, 1, '#161c24');     // bogies
  p.rect(c + 2, g - 11, 14, 1, CR.dk);                                                // sill beam
  p.line(c + 2, g - 25, c + 15, g - 12, CR.dk); p.line(c + 15, g - 25, c + 2, g - 12, CR.dk);
  p.rect(c - 1, CB + 4, 20, 1, CR.hi); p.rect(c - 1, CB + 5, 20, 1, CR.mid);          // portal beam
  const boomRow = (x0, x1) => {
    for (let i = x0; i <= x1; i++) { p.set(i, CB, CR.hi); if ((i & 1) === 0) p.set(i, CB + 1, CR.mid); p.set(i, CB + 2, CR.mid); }
  };
  boomRow(c, c + 27);                                                 // back reach
  p.set(c, CB + 3, CR.mid); p.set(c + 17, CB + 3, CR.mid);
  p.line(c + 3, CB - 1, c + 12, APEX, CR.mid); p.line(c + 17, CB - 1, c + 13, APEX, CR.dk);   // A-frame
  p.rect(c + 12, APEX - 1, 2, 2, CR.hi);
  p.line(c + 13, APEX, c + 27, CB - 1, CR.dk);                        // backstay
  p.rect(c + 18, CB - 5, 10, 4, CR.house); p.rect(c + 18, CB - 5, 10, 1, CR.hi);        // machinery house
  for (let i = c + 19; i < c + 27; i += 2) if (r() < 0.6) p.set(i, CB - 3, '#c8a070');
  if (cr.type === 'flat') {
    boomRow(c - BOOM_LEN, c - 1);
    p.line(c + 12, APEX, c - 22, CB - 1, CR.dk); p.line(c + 12, APEX, c - 8, CB - 1, CR.dk);   // forestays
  } else if (cr.type === 'up') {
    const [tx, ty] = upTip(c, UP_TH);
    p.line(c, CB + 1, tx, ty, CR.hi); p.line(c - 1, CB + 1, tx - 1, ty, CR.mid);
    p.line(c + 12, APEX, tx + 1, ty + 2, CR.dk);
  }
}
function renderFar() {
  const p = painter(), r = rng(17);
  for (let x = 0; x < 300;) {                                         // low sheds and silos on the far shore
    const w = 6 + Math.floor(r() * 16), top = 145 + Math.floor(r() * 9);
    p.rect(x, top, w, RAIL - top, r() < 0.5 ? '#232c36' : '#27313b');
    if (r() < 0.4) p.rect(x + 2, top - 4, 3, 4, '#232c36');
    x += w;
  }
  for (const fx of [44, 88, 130, 178, 226, 270]) {                    // a second row of cranes, far off in the rain
    p.rect(fx, 140, 1, 12, '#2c3844'); p.rect(fx + 7, 140, 1, 12, '#2c3844');
    p.rect(fx - 9, 139, 22, 1, '#34404c'); p.line(fx + 4, 132, fx - 7, 139, '#2c3844'); p.line(fx + 4, 132, fx + 12, 139, '#2c3844');
  }
  for (let y = RAIL; y < HOR; y++) for (let i = 0; i < 292; i++)       // terminal apron and the quay edge
    p.set(i, y, y === HOR - 1 ? (i % 7 === 0 ? '#2a2c30' : '#5a5048') : y === RAIL ? '#3a3a3e' : '#1c2128');
  for (let x = 0; x < 292; x += 5) {                                  // container stacks
    const tiers = 1 + Math.floor(r() * 5);
    for (let k = 0; k < tiers; k++) {
      const [b, tp] = CONT[Math.floor(r() * CONT.length)], y = RAIL - 2 - k * 2;
      p.rect(x, y, 4, 1, k === tiers - 1 ? tp : b); p.rect(x, y + 1, 4, 1, b);
      if (r() < 0.3) p.set(x + 1 + Math.floor(r() * 2), y + 1, '#1a1e24');
    }
  }
  for (const m of MASTS) { p.rect(m, 121, 1, RAIL - 121, '#2a323c'); p.rect(m - 1, 120, 3, 1, '#ffe6b8'); p.set(m, 119, '#8a8070'); }
  for (const cr of CRANES) craneStatic(p, cr, r);
  // a feeder moored under the cranes: dark hull, three tiers of boxes, the bridge aft
  for (let y = 153; y < HOR; y++) {
    const x0 = 4 + Math.max(0, y - 157), x1 = 116 - Math.max(0, (y - 153) * 1);
    p.rect(x0, y, x1 - x0 + 1, 1, y === 153 ? '#4a3a3e' : y >= 158 ? '#3a1c1e' : '#221a1e');
  }
  for (let x = 8; x < 100; x += 5) {
    const tiers = 1 + Math.floor(r() * 3);
    for (let k = 0; k < tiers; k++) { const [b, tp] = CONT[Math.floor(r() * CONT.length)]; p.rect(x, 151 - k * 2, 4, 1, tp); p.rect(x, 152 - k * 2, 4, 1, b); }
  }
  p.rect(103, 139, 10, 14, '#7a8088'); p.rect(103, 139, 10, 1, '#a0a6ae'); p.rect(112, 140, 1, 13, '#565c66');
  for (const wy of [141, 144, 147]) for (let i = 104; i < 111; i += 2) p.set(i, wy, r() < 0.7 ? '#ffd490' : '#3a3e46');
  p.rect(100, 138, 16, 1, '#8a9098'); p.rect(107, 132, 1, 6, '#3a3e46');
  return p.done();
}
/* rain haze that pushes the far bank back (dithered, drawn with alpha) */
function renderHaze() {
  const p = painter();
  for (let y = 92; y < HOR + 2; y++) for (let i = 0; i < W; i++) {
    const v = smooth(92, 156, y) * 0.9;
    if (v > bayer(i, y) * 0.95) p.set(i, y, '#5a6c7c');
  }
  return p.done();
}

/* ================= Elbphilharmonie =================
   The old brick Kaispeicher (a grid of small windows, floodlit from the quay), the Plaza (the open terrace between
   brick and glass, a warm band all round the building at night) and the glass crown: curved panels a floor high,
   horseshoe loggias and a roofline of concave sweeps rising to sharp peaks, highest at the prow corner. The narrow
   end face (dx < EP.end) is darker than the long face. */
const EP_ROOF = [[0, 81], [14, 58], [52, 68], [85, 78], [109, 87], [118, 95]];   // (dx, y): ends and peaks
const EP_SAG = [2, 22, 19, 14, 3];                                              // depth of each concave sweep
function roofY(dx) {
  for (let k = 0; k < EP_ROOF.length - 1; k++) {
    const [xa, ya] = EP_ROOF[k], [xb, yb] = EP_ROOF[k + 1];
    if (dx <= xb) {
      const u = clamp((dx - xa) / (xb - xa), 0, 1);
      return ya + (yb - ya) * u + EP_SAG[k] * Math.pow(Math.sin(Math.PI * u), 0.8);
    }
  }
  return EP_ROOF[EP_ROOF.length - 1][1];
}
const GL = ['#111828', '#1a2439', '#26344f', '#374a6b', '#4d648a', '#6a84ab', '#8ea9cc', '#b6cfe8', '#dcebf9', '#f6fbff'];
const GLW = ['#8a7a72', '#c8a47e', '#ecc892', '#fff0c8'];   // a curve catching the port's sodium glow
// a pane's curve: how its 3x3 of bent glass catches the light (rows top to bottom). Bulging out it shines at a top
// corner or along an arched top and falls away below; dished in it shines along its lower edge; bent upright it
// shines down one side
const FACETS = [
  [0.3, 0.2, 0.04, 0.2, 0.02, -0.1, 0.04, -0.1, -0.16],
  [0.04, 0.2, 0.3, -0.1, 0.02, 0.2, -0.16, -0.1, 0.04],
  [0.12, 0.3, 0.12, 0.02, 0.1, 0.02, -0.12, -0.12, -0.12],
  [-0.12, -0.14, -0.12, -0.02, 0.02, -0.02, 0.14, 0.26, 0.14],
  [0.24, 0.04, -0.12, 0.24, 0.04, -0.12, 0.2, 0.02, -0.12],
];
const PZW = ['#5a3626', '#8a5232', '#bc7a42', '#e2a25a', '#f6c47c', '#ffe2a6'];   // the Plaza's warm light
const BRK = ['#211110', '#2e1713', '#3d1e18', '#4e271e', '#633024', '#7a3d2c', '#945036'];
const WARM_A = '#ffd88e', WARM_B = '#eea45e';
const LOGGIAS = [[28, 96], [60, 110], [92, 118], [42, 124], [76, 126], [104, 122]];   // horseshoe cut-outs (dx, top y)
const EP_RED = [[EP.x0 + EP.end, 56], [EP.x0 + 52, 66]];                            // aviation lights on the peaks
const EP_LAMPS = [184, 206, 228, 250, 272];                                          // quay lamps in front of it
function renderElbphi() {
  const p = painter(), WD = EP.x1 - EP.x0, m = painter(WD + 1, EP.glass + 1), r = rng(88);
  const warm = [], twinkle = [], X0 = EP.x0;
  const tops = [];
  for (let dx = 0; dx <= WD; dx++) tops.push(Math.round(roofY(dx)));
  // --- glass crown: panes a floor (4 rows) high and 4 columns wide, mullion and slab included. Each mirrors the night
  // its own way: most the cool sky, some the port's sodium glow (in patches, more of them low and towards the prow),
  // some a dark room; and each bulge puts its highlight somewhere else
  const pane = (pc, row, endF, dx) => {
    const id = pc + (endF ? 500 : 0), h = hash(id, row, 91), k = hash(id, row, 92), facet = FACETS[Math.floor(hash(id, row, 94) * FACETS.length)];
    const patch = hash(Math.floor((pc + (row & 1)) / 3) + (endF ? 50 : 0), Math.floor(row / 2), 93);
    const warmP = (0.03 + 0.3 * patch * patch) * (1.3 - dx / WD) + (row < 5 ? 0.05 : 0);
    if (h < warmP) return { warm: true, dv: -0.08 + (k - 0.5) * 0.1, facet, th: hash(id, row, 95) };
    if (h > 0.93 - 0.06 * patch) return { dv: -0.18 - k * 0.06, facet, fs: 0.4, th: hash(id, row, 95) };
    return { dv: (k - 0.5) * 0.18 + (patch - 0.5) * 0.1 + (k > 0.9 ? 0.14 : 0), facet, th: hash(id, row, 95) };
  };
  for (let dx = 0; dx <= WD; dx++) {
    const top = tops[dx], i = X0 + dx, endF = dx < EP.end, lx = endF ? dx : dx - EP.end, pc = Math.floor(lx / 4), c4 = lx % 4;
    for (let y = top; y <= EP.glass; y++) {
      const fy = EP.glass - y, row = Math.floor(fy / 4), ry = fy % 4, f = (y - top) / Math.max(1, EP.glass - top);
      const P = pane(pc, row, endF, dx);
      let v = (endF ? 0.26 + 0.1 * f : 0.42 + 0.14 * f - 0.1 * (dx / WD)) + P.dv, col = null;
      if (ry === 0) v = v - P.dv * 0.5 - 0.24;                                      // the floor slab
      else if (c4 === 0) v = v - P.dv * 0.5 - 0.14;                                 // the mullion
      else {                                                                        // the pane: one dither step for all
        const fc = P.facet[(3 - ry) * 3 + c4 - 1];                                  // of it, so its curve reads clean
        v += 0.05 + fc * (P.fs ?? 0.75);
        const q = clamp(v, 0, 1) * (GL.length - 1);
        col = GL[Math.min(GL.length - 1, Math.floor(q) + (q % 1 > P.th ? 1 : 0))];
        if (P.warm && fc > 0.1) col = GLW[clamp(Math.round((fc - 0.1) * 12 + (endF ? -1 : 1) + P.th), 0, GLW.length - 1)];
      }
      if (y - top < 1) { v = 1; col = null; } else if (y - top < 2) { v = 0.84; col = null; }   // the lit roof lip
      if (dx === EP.end && y - top >= 2) { v = Math.max(v, 0.74); col = null; }                 // prow corner
      p.set(i, y, col ?? dpick(GL, v, i, y));
      if (!endF && y - top >= 2) m.set(dx, y, '#ffffff');
    }
  }
  // lit rooms and foyers: warm runs of panes along the floors (some switch on and off), and twinkle spots
  for (let row = 0; row < 20; row++) {
    const y0 = EP.glass - row * 4 - 2;
    let run = 0, rw = null;
    for (let dx = EP.end + 1; dx < WD - 2; dx += 4) {
      const top = Math.max(tops[dx], tops[dx + 1], tops[dx + 2]);
      if (y0 - 1 < top + 2) { run = 0; continue; }
      if (run <= 0) {
        const pr = row < 2 ? 0.3 : y0 < top + 12 ? 0.2 : 0.07;
        if (r() < pr) {
          run = 2 + Math.floor(r() * 4);
          rw = r() < 0.55 ? null : { id: warm.length, cells: [], y: y0 - 1, n: 2 + Math.floor(r() * 6), p: 0.5, off: r() };
          if (rw) warm.push(rw);
        } else {
          if (r() < 0.08) twinkle.push({ id: twinkle.length, x: X0 + dx + (r() < 0.5 ? 1 : 2), y: y0 - (r() < 0.5 ? 1 : 0) });
          continue;
        }
      }
      run--;
      if (rw) rw.cells.push(X0 + dx);
      else for (let k = 0; k < 3; k++) { p.set(X0 + dx + k, y0 - 1, WARM_A); p.set(X0 + dx + k, y0, WARM_B); }
    }
  }
  // horseshoe loggias: dark recesses with a bright rim and a warm floor
  for (const [ldx, ly] of LOGGIAS) {
    const x = X0 + ldx;
    const shape = ['.XXXX.', 'XXXXXX', 'XXXXXX', 'XXXXXX', 'XXXXXX', 'XXXXXX', 'XXXXXX'];
    shape.forEach((row, j) => { for (let k = 0; k < 6; k++) if (row[k] === 'X') p.set(x + k, ly + j, j >= 5 ? (j === 5 ? '#ffc478' : '#c07840') : '#0a0f1c'); });
    for (let k = 1; k < 5; k++) p.set(x + k, ly - 1, GL[8]);
    p.set(x, ly, GL[7]); p.set(x + 5, ly, GL[7]);
    p.set(x + 2, ly + 4, '#3a2a2a'); p.set(x + 2, ly + 3, '#2a2028');                  // someone looking out
  }
  // --- the Plaza: the glass's dark soffit with its downlights, the open terrace glowing warm under it in pools of
  // light from the ceiling, and the lit rail of its balustrade on the brick
  for (let dx = 0; dx <= WD; dx++) {
    const i = X0 + dx, endF = dx < EP.end, k = endF ? -0.24 : 0, pool = 0.5 + 0.5 * Math.cos(TAU * (dx - 4) / 9);
    p.set(i, PZ.y, (dx - 4) % 9 === 0 ? (endF ? '#a87850' : '#f2cc90') : '#120e14');
    for (let j = 1; j <= 3; j++) p.set(i, PZ.y + j, dpick(PZW, [0.86, 0.68, 0.46][j - 1] + 0.14 * pool + k, i, PZ.y + j));
    p.set(i, PZ.y + 4, dpick(['#5a3424', '#8a5a3e', '#b47e56'], 0.6 + 0.3 * pool + k, i, PZ.y + 4));
  }
  // --- the brick Kaispeicher with its grid of small windows
  for (let y = EP.top; y < EP.base; y++) for (let dx = 0; dx <= WD; dx++) {
    const i = X0 + dx, endF = dx < EP.end, lx = endF ? dx : dx - EP.end;
    const f = (y - EP.top) / (EP.base - EP.top);
    let v = 0.18 + 0.56 * Math.pow(f, 1.6) - (endF ? 0.14 : 0);
    if ((y & 1) === 0 && hash(Math.floor((i + ((y >> 1) & 1) * 2) / 4), y, 7) < 0.3) v += 0.07;
    let col = dpick(BRK, v, i, y);
    const wy = (y - EP.top - 3) % 5, wx = lx % 4;
    if (y >= EP.top + 3 && y < EP.base - 4 && wy < 2 && (wx === 1 || wx === 2)) {
      const lit = hash(Math.floor(lx / 4) + (endF ? 300 : 0), Math.floor((y - EP.top - 3) / 5), 5) < 0.1;
      col = lit ? (wy === 0 ? '#e0a066' : '#b87848') : (wy === 0 ? '#120909' : '#1c100e');
    }
    if (dx === EP.end) col = dpick(BRK, v + 0.18, i, y);
    p.set(i, y, col);
  }
  for (const lx of EP_LAMPS) { p.set(lx, EP.base - 1, '#fff0c8'); p.set(lx, EP.base - 2, '#3a2a24'); }
  // --- its quay
  for (let dx = -2; dx <= WD + 2; dx++) {
    const i = X0 + dx;
    p.set(i, EP.base, '#6a6e78'); p.set(i, EP.base + 1, '#474c56');
    for (let y = EP.base + 2; y < EP.wl; y++) p.set(i, y, (dx + (y & 1) * 5) % 10 === 0 ? '#1c1f26' : '#2c3038');
  }
  return { c: p.done(), mask: m.done(), warm, twinkle };
}
/* a soft diagonal sheen that sweeps across the glass now and then (drawn through the glass mask) */
function renderSheen() {
  const h = EP.glass + 1, w = 36 + h, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const u = (i - (h - y) * 0.45) / 36;
    if (u < 0 || u > 1) continue;
    const s = Math.sin(Math.PI * u) * (0.6 + 0.4 * Math.sin(u * 17));
    if (s > bayer(i, y) + 0.15) p.set(i, y, '#7fb4e8');
  }
  return p.done();
}

/* ================= Speicherstadt: floodlit neo-Gothic brick, copper turrets, loading bays, hoists ================= */
const SB = ['#2a1210', '#3c1914', '#52221a', '#6a2c20', '#843828', '#a04832', '#bc5e3e', '#d4784e', '#e8986a'];
const CU = ['#1a3834', '#27564c', '#3a7a6a', '#56a08c', '#80c6ae', '#b0e4cf'];
const TRIM = ['#5a4a40', '#8a7058', '#b8987a', '#e2c8a2'];
const GRAN = ['#22232a', '#2e2f38', '#3e3f4a', '#55565f'];
const NB_SPOTS = [398, 446, 472];
const FB_SPOTS = [300, 330];
function brickV(i, y) {
  const row = Math.floor(y / 3);
  if (y % 3 === 2) return -0.13;
  if ((i + (row & 1) * 3) % 6 === 0) return -0.09;
  return (hash(Math.floor((i + (row & 1) * 3) / 6), row, 3) - 0.5) * 0.1;
}
/** warm floodlight falling on a facade: cones from lamps at the waterline plus a general wash */
function floodV(i, y, base, spots, k = 1) {
  let v = 0.2 + 0.32 * smooth(base - 90, base, y);
  for (const s of spots) v += 0.34 * Math.max(0, 1 - Math.abs(i - s) / (5 + (base - y) * 0.36)) * Math.max(0, 1 - (base - y) / 190);
  return v * k;
}
/** pointed-arch window: glass w x h with a single-pixel point, a sandstone hood above */
function lancet(p, x, y, w, h, glass, hood) {
  const mid = x + (w >> 1);
  p.set(mid, y, glass);
  for (let j = 1; j < h; j++) for (let k = 0; k < w; k++) p.set(x + k, y + j, glass);
  if (hood) { p.set(mid, y - 1, hood); p.set(mid - 1, y, hood); p.set(mid + 1, y, hood); }
}
/** copper spire: centre cx, from base y0 up to tip y1, half-width hw at the base */
function spire(p, cx, y0, y1, hw, shade = 1) {
  for (let y = y1; y <= y0; y++) {
    const h = Math.round(hw * (y - y1) / (y0 - y1));
    for (let dx = -h; dx <= h; dx++) {
      let v = (0.72 - 0.5 * (dx + h) / Math.max(1, 2 * h)) * shade;
      if (dx === -h) v += 0.2;
      if ((y - y1) % 6 === 0) v -= 0.12;
      p.set(cx + dx, y, dpick(CU, v, cx + dx, y));
    }
  }
}
function renderNear() {
  const p = painter(), r = rng(606), wins = [];
  const X0 = NB.x0, B = NB.base, TOPW = 86, PL = B - 14;
  const lightAt = (i, y) => floodV(i, y, B, NB_SPOTS) + (y < 100 ? 0.22 * Math.max(0, 1 - Math.abs(i - 446) / 34) : 0);
  const wall = (i, y, dv = 0) => p.set(i, y, dpick(SB, lightAt(i, y) + brickV(i, y) + dv, i, y));
  // main wall
  for (let y = TOPW; y < PL; y++) for (let i = X0; i < W; i++) wall(i, y, i < 404 ? 0.05 - (i - 392) * 0.02 : 0);
  // stepped gable with pinnacles and blind lancet niches
  const GABLE = [[80, 86, 418, 474], [72, 80, 423, 469], [64, 72, 428, 464], [56, 64, 433, 459], [50, 56, 439, 453]];
  for (const [y0, y1, a, b] of GABLE) {
    for (let y = y0; y < y1; y++) for (let i = a; i <= b; i++) wall(i, y);
    for (let i = a; i <= b; i++) p.set(i, y0, dpick(TRIM, lightAt(i, y0) + 0.3, i, y0));
    for (const e of [a, b]) {
      for (let y = y0 - 4; y < y0; y++) p.set(e, y, dpick(TRIM, lightAt(e, y), e, y));
      p.set(e, y0 - 5, CU[4]); p.set(e, y0 - 6, CU[3]);
    }
  }
  for (const [nx, ny, nh] of [[426, 74, 6], [432, 73, 7], [460, 73, 7], [466, 74, 6], [437, 65, 6], [455, 65, 6]])
    for (let y = ny; y < ny + nh; y++) { p.set(nx, y, dpick(TRIM, lightAt(nx, y) - 0.1, nx, y)); if (y > ny) p.set(nx + 1, y, dpick(TRIM, lightAt(nx, y) - 0.25, nx, y)); }
  for (let y = 57; y <= 63; y++) for (let i = 443; i <= 449; i++) {           // round window, lit
    const d = Math.hypot(i - 446, y - 60);
    if (d < 3.6) p.set(i, y, d > 2.6 ? TRIM[2] : d < 1.2 ? '#fff0c8' : '#f0b068');
  }
  for (let y = 42; y < 50; y++) p.set(446, y, dpick(TRIM, 0.6, 446, y));      // apex pinnacle and flagpole
  p.set(445, 49, TRIM[1]); p.set(447, 49, TRIM[1]); p.set(446, 41, CU[4]);
  for (let y = 20; y < 41; y++) p.set(446, y, y < 22 ? '#d8d8dc' : '#8a8a92');
  p.rect(443, 66, 7, 2, '#2a1a12'); p.set(443, 66, '#5a3a24'); p.set(446, 68, '#8a8a90');   // hoist beam + pulley
  // copper roof slopes beside the gable
  for (let i = 404; i < 418; i++) { const top = Math.round(84 - (i - 404) * 0.5); for (let y = top; y < TOPW; y++) p.set(i, y, dpick(CU, y === top ? 0.8 : 0.3 - (y - top) * 0.04, i, y)); }
  for (let i = 475; i < W; i++) for (let y = 80; y < TOPW; y++) p.set(i, y, dpick(CU, y === 80 ? 0.75 : 0.28, i, y));
  // cornice with a corbel frieze
  for (let i = X0; i < W; i++) {
    p.set(i, TOPW, dpick(TRIM, lightAt(i, TOPW) + 0.25, i, TOPW)); p.set(i, TOPW + 1, '#2a1612');
    p.set(i, TOPW + 2, i % 3 === 0 ? '#2a1612' : dpick(TRIM, lightAt(i, TOPW) - 0.05, i, TOPW + 2));
    p.set(i, TOPW + 3, dpick(TRIM, lightAt(i, TOPW) - 0.15, i, TOPW + 3));
  }
  // string courses and six floors of windows; a column of loading doors in the middle
  const FLOORS = [90, 106, 122, 138, 154, 170];
  for (const y0 of FLOORS) {
    const sc = y0 + 14;
    for (let i = X0; i < W; i++) { p.set(i, sc, dpick(TRIM, lightAt(i, sc) - 0.05, i, sc)); p.set(i, sc + 1, '#2a1612'); }
    for (const wx of [397, 405, 413, 423, 431, 461, 469, 477]) {
      const q = r(), lit = q < 0.2;
      lancet(p, wx, y0 + 2, 3, 10, lit ? WARM_B : '#15101a', dpick(TRIM, lightAt(wx, y0), wx, y0));
      if (lit) { p.set(wx, y0 + 4, WARM_A); p.set(wx + 1, y0 + 4, WARM_A); p.set(wx + 2, y0 + 4, WARM_A); p.set(wx + 1, y0 + 3, WARM_A); }
      else { p.set(wx + 1, y0 + 6, '#2e2a38'); if (q < 0.36) wins.push({ id: wins.length, x: wx, y: y0 + 2, n: 2 + Math.floor(r() * 5), p: 0.5, off: r() }); }
      p.set(wx, y0 + 7, '#3a3040'); p.set(wx + 2, y0 + 7, '#3a3040');
    }
    for (let y = y0; y < y0 + 13; y++) { p.set(440, y, dpick(SB, lightAt(440, y) - 0.25, 440, y)); p.set(451, y, dpick(SB, lightAt(451, y) - 0.25, 451, y)); }
    const open = r() < 0.25;
    for (let y = y0 + 1; y < y0 + 13; y++) for (let i = 441; i <= 450; i++) {
      let col = (i === 441 || i === 450 || y === y0 + 1) ? dpick(TRIM, lightAt(i, y) - 0.1, i, y) : (i === 445 || i === 446) ? '#16241c' : (i & 1) ? '#223a2e' : '#2c4838';
      if (open && i > 441 && i < 450 && y > y0 + 1) col = y > y0 + 9 ? '#c8803e' : '#f0b060';
      p.set(i, y, col);
    }
  }
  // corner tower: its own shading, small windows, a pierced gallery and a tall copper spire
  for (let y = 68; y < PL; y++) for (let i = 392; i < 404; i++) {
    const dx = i - 392;
    let v = lightAt(i, y) + 0.22 - dx * 0.045 + brickV(i, y) * 0.6;
    if (dx === 0) v += 0.18; if (dx === 11) v -= 0.2;
    p.set(i, y, dpick(SB, v, i, y));
  }
  for (const wy of [74, 92, 110, 128, 146, 162, 178]) lancet(p, 396, wy, 3, 8, wy === 110 || wy === 162 ? WARM_B : '#140f18', TRIM[2]);
  for (let i = 391; i < 405; i++) { p.set(i, 66, TRIM[3]); p.set(i, 67, (i & 1) ? TRIM[2] : '#241410'); p.set(i, 68, TRIM[1]); }
  spire(p, 397, 65, 30, 6);
  for (let y = 23; y < 30; y++) p.set(397, y, '#8a8a70');
  p.set(396, 26, '#c8b060'); p.set(397, 26, '#e8d080'); p.set(398, 26, '#a89040'); p.set(397, 25, '#c8b060');
  for (let y = 52; y <= 56; y++) for (let i = 395; i <= 399; i++) p.set(i, y, y === 52 ? CU[4] : (i === 397 && y > 53) ? '#ffd090' : CU[2]);   // lucarne
  // granite plinth standing in the Fleet, with arched water gates
  for (let y = PL; y < B; y++) for (let i = X0; i < W; i++) {
    let col = dpick(GRAN, 0.55 - (y - PL) * 0.03 + ((i + (((y - PL) >> 2) & 1) * 6) % 12 === 0 ? -0.3 : 0), i, y);
    if (y === PL) col = GRAN[3];
    for (const gx of [412, 456]) {
      const dx = i - gx, dy = y - (PL + 4);
      if (Math.abs(dx) <= 4 && (dy >= 0 || Math.hypot(dx, dy) <= 4.2)) col = Math.abs(dx) === 4 || (dy < 0 && Math.hypot(dx, dy) > 3.3) ? GRAN[3] : '#0b0c12';
    }
    p.set(i, y, col);
  }
  return { c: p.done(), wins };
}

function renderFarBlock() {
  const p = painter(), r = rng(707), wins = [];
  const X0 = FB.x0, X1 = FB.x1, B = FB.base, TOPW = 128, PL = B - 8, K = 0.86;
  const lightAt = (i, y) => floodV(i, y, B, FB_SPOTS, K) + (y < TOPW + 6 ? 0.12 : 0);
  const wall = (i, y, dv = 0) => p.set(i, y, dpick(SB, lightAt(i, y) + brickV(i, y) * 0.8 + dv, i, y));
  for (let y = TOPW; y < PL; y++) for (let i = X0; i <= X1; i++) wall(i, y, i === X0 ? 0.15 : i === X1 ? -0.2 : 0);
  // left: stepped gable; right: pointed gable; a slim turret between them
  for (const [y0, y1, a, b] of [[122, 128, 290, 312], [116, 122, 294, 308], [111, 116, 298, 304]]) {
    for (let y = y0; y < y1; y++) for (let i = a; i <= b; i++) wall(i, y);
    for (let i = a; i <= b; i++) p.set(i, y0, dpick(TRIM, lightAt(i, y0) + 0.2, i, y0));
    for (const e of [a, b]) { p.set(e, y0 - 1, TRIM[2]); p.set(e, y0 - 2, CU[3]); }
  }
  for (let y = 108; y < 111; y++) p.set(301, y, TRIM[2]);
  for (let y = 112; y < TOPW; y++) {
    const hw = Math.round((y - 112) * 0.72);
    for (let i = 332 - hw; i <= 332 + hw; i++) if (i <= X1) wall(i, y);
    if (332 - hw >= 320) p.set(332 - hw, y, dpick(TRIM, lightAt(332, y) + 0.1, 332 - hw, y));
    if (332 + hw <= X1) p.set(332 + hw, y, dpick(TRIM, lightAt(332, y) - 0.1, 332 + hw, y));
  }
  for (let y = 116; y <= 122; y++) for (let i = 330; i <= 334; i++) { const d = Math.hypot(i - 332, y - 119); if (d < 2.6) p.set(i, y, d < 1.4 ? '#f0b068' : TRIM[2]); }
  for (let y = 100; y < TOPW; y++) for (let i = 314; i <= 318; i++) p.set(i, y, dpick(SB, lightAt(i, y) + 0.1 - (i - 314) * 0.06, i, y));
  for (let i = 313; i <= 319; i++) p.set(i, 100, TRIM[2]);
  spire(p, 316, 99, 84, 3, 0.9);
  p.set(316, 83, '#c8b060'); p.set(316, 82, '#8a8a70');
  for (let i = X0; i <= X1; i++) { p.set(i, TOPW, dpick(TRIM, lightAt(i, TOPW) + 0.2, i, TOPW)); p.set(i, TOPW + 1, '#2a1612'); }
  // floors of small windows, loading doors, string courses
  for (const y0 of [131, 143, 155, 167, 179]) {
    for (let i = X0; i <= X1; i++) p.set(i, y0 + 10, dpick(TRIM, lightAt(i, y0 + 10) - 0.1, i, y0 + 10));
    for (const wx of [292, 297, 302, 307, 325, 330, 335, 340]) {
      const q = r(), lit = q < 0.18;
      lancet(p, wx, y0 + 2, 2, 7, lit ? WARM_B : '#15101a', null);
      if (lit) { p.set(wx, y0 + 3, WARM_A); p.set(wx + 1, y0 + 3, WARM_A); }
      else if (q < 0.34) wins.push({ id: wins.length, x: wx, y: y0 + 3, n: 2 + Math.floor(r() * 5), p: 0.5, off: r() });
    }
    const open = r() < 0.2;
    for (let y = y0 + 1; y < y0 + 10; y++) for (let i = 313; i <= 319; i++)
      p.set(i, y, (i === 313 || i === 319) ? dpick(TRIM, lightAt(i, y) - 0.15, i, y) : open ? (y > y0 + 7 ? '#c07a3a' : '#e8a858') : (i === 316 ? '#16241c' : '#24402f'));
  }
  for (let y = PL; y < B; y++) for (let i = X0; i <= X1; i++)
    p.set(i, y, y === PL ? GRAN[3] : dpick(GRAN, 0.5 - (y - PL) * 0.04 + ((i + (((y - PL) >> 2) & 1) * 5) % 10 === 0 ? -0.3 : 0), i, y));
  return { c: p.done(), wins };
}

/* ================= Down the Fleet: far warehouses and the Michel's copper tower ================= */
const MICU = ['#2a403f', '#3a5452', '#4c6a66', '#648480', '#86a29a', '#a8bcb4'];
const MIBR = ['#2e2224', '#402c2c', '#543a36', '#6a4a42', '#7e5a4e'];
function renderGap() {
  const p = painter(), r = rng(808), cx = MI.cx, wins = [];
  const rowC = (y, hw, pal, fn) => { for (let dx = -hw; dx <= hw; dx++) p.set(cx + dx, y, dpick(pal, fn(dx, hw), cx + dx, y)); };
  const cyl = (dx, hw) => 0.72 - 0.5 * (dx + hw) / Math.max(1, 2 * hw);
  // (smaller and hazier than the near turrets: it stands a kilometre off, at the end of the Fleet)
  for (let y = MI.tip; y < MI.tip + 3; y++) p.set(cx, y, y === MI.tip + 1 ? '#c8c098' : '#8a9088');   // cross
  p.set(cx - 1, MI.tip + 1, '#a8a488'); p.set(cx + 1, MI.tip + 1, '#a8a488');
  for (let y = MI.tip + 3; y < MI.tip + 14; y++) rowC(y, Math.round((y - MI.tip - 3) / 11 * 1.4), MICU, cyl);   // slim spire
  for (let y = MI.tip + 14; y < MI.tip + 20; y++) {                                 // lit lantern
    rowC(y, 2, MICU, cyl);
    if (y > MI.tip + 14 && y < MI.tip + 19) { p.set(cx - 1, y, '#e8d0a0'); p.set(cx + 1, y, '#d8b880'); }
  }
  for (let y = MI.tip + 20; y < MI.tip + 25; y++) rowC(y, Math.round(2 + (y - MI.tip - 20) * 0.6), MICU, (dx, hw) => cyl(dx, hw) + (y === MI.tip + 20 ? 0.2 : 0));   // cupola
  for (let y = MI.tip + 25; y < MI.tip + 35; y++) rowC(y, 4, MICU, (dx, hw) => (y === MI.tip + 25 || y === MI.tip + 34) ? 0.85 : (dx + 4) % 3 === 0 ? cyl(dx, hw) + 0.2 : 0);
  for (let y = MI.tip + 27; y < MI.tip + 33; y++) for (const dx of [-3, -2, 0, 2, 3]) p.set(cx + dx, y, y > MI.tip + 30 ? '#c89868' : '#e8c890');   // belfry light
  for (let y = MI.tip + 35; y < MI.tip + 45; y++) rowC(y, 5, MICU, (dx, hw) => (y === MI.tip + 35 || y === MI.tip + 44) ? 0.8 : cyl(dx, hw) + ((dx === -5 || dx === 5) ? 0.25 : 0));
  for (let y = MI.clock - 3; y <= MI.clock + 3; y++) for (let i = cx - 3; i <= cx + 3; i++) {   // the clock (hands per frame)
    const d = Math.hypot(i - cx, y - MI.clock);
    if (d < 3.3) p.set(i, y, d > 2.5 ? '#3a3a30' : '#e4dcc4');
  }
  for (let y = MI.tip + 45; y < 140; y++) rowC(y, 6, MIBR, (dx, hw) => cyl(dx, hw) + (y === MI.tip + 45 ? 0.3 : 0) + ((y - MI.tip - 45) % 8 === 0 ? 0.12 : 0));
  for (const wy of [MI.tip + 50, MI.tip + 61]) for (let y = wy; y < wy + 5; y++) p.set(cx, y, '#241a1e');
  // far canal-side warehouses in the rain, gables and a few lit windows
  const far = [[344, 358, 134, 128], [356, 372, 140, 131], [370, 384, 137, 129], [382, 392, 132, 126]];
  for (const [a, b, top, peak] of far) {
    const mid = (a + b) / 2;
    for (let i = a; i < b; i++) {
      const t0 = Math.round(top + (peak - top) * Math.max(0, 1 - Math.abs(i - mid) / ((b - a) / 2)));
      for (let y = t0; y < 190; y++) {
        const v = 0.3 + 0.35 * smooth(150, 190, y) + (bayer(i, y) - 0.5) * 0.1;
        p.set(i, y, dpick(MIBR, v, i, y));
      }
      p.set(i, t0, MIBR[4]);
    }
    for (let wy = top + 6; wy < 184; wy += 7) for (let wx = a + 2; wx < b - 1; wx += 3) {
      const q = r();
      if (q < 0.18) p.set(wx, wy, q < 0.1 ? '#f0b060' : '#c88848');
      else if (q < 0.3) wins.push({ id: wins.length, x: wx, y: wy, n: 2 + Math.floor(r() * 5), p: 0.5, off: r() });
      else p.set(wx, wy, '#1a1014');
    }
  }
  for (let y = 190; y < FB.base; y++) for (let i = 344; i < NB.x0; i++) p.set(i, y, dpick(['#0c1620', '#101c26', '#16242e'], 0.4 + (bayer(i, y) - 0.5) * 0.3, i, y));
  return { c: p.done(), wins };
}

/* ================= The iron footbridge: arched lattice girder, X-braced railing, two lamps ================= */
const IRON = { hi: '#86a498', mid: '#4e6c62', dk: '#2c423c', vdk: '#1a2a26' };
const BRG_LAMPS = [343, 392];
function renderBridge() {
  const p = painter(), D = BRG.deck;
  for (const ax of [BRG.x0 - 2, BRG.x1 - 3]) for (let y = D - 1; y < FB.base; y++) for (let i = ax; i < ax + 6; i++)
    p.set(i, y, y === D - 1 ? GRAN[3] : dpick(GRAN, 0.55 - (i - ax) * 0.06 + ((y - D) % 4 === 0 ? -0.25 : 0), i, y));
  for (let i = BRG.x0 + 3; i < BRG.x1 - 3; i++) {
    const u = (i - BRG.x0 - 3) / (BRG.x1 - BRG.x0 - 7), yb = Math.round(D + 8 - 5 * Math.sin(Math.PI * u));
    p.set(i, D, IRON.hi); p.set(i, D + 1, IRON.mid);
    for (let y = D + 2; y < yb; y++) if ((i + y) % 5 === 0 || (i - y) % 5 === 0) p.set(i, y, IRON.dk);
    p.set(i, yb, IRON.hi); p.set(i, yb + 1, IRON.dk);
    p.set(i, D - 7, IRON.hi); p.set(i, D - 6, IRON.mid);                       // handrail
    for (let y = D - 5; y < D; y++) if ((i + y) % 4 === 0 || (i - y) % 4 === 0) p.set(i, y, IRON.mid);
    if ((i - BRG.x0) % 8 === 0) for (let y = D - 6; y < D; y++) p.set(i, y, IRON.dk);
  }
  for (const lx of BRG_LAMPS) {
    for (let y = D - 20; y < D; y++) p.set(lx, y, y > D - 3 ? IRON.dk : IRON.vdk);
    p.set(lx - 1, D - 8, IRON.vdk); p.set(lx + 1, D - 8, IRON.vdk);
    p.rect(lx - 1, D - 25, 3, 1, IRON.vdk); p.set(lx, D - 26, IRON.vdk);
    for (let y = D - 24; y < D - 20; y++) { p.set(lx - 1, y, IRON.vdk); p.set(lx, y, y < D - 22 ? '#fff4d0' : '#ffd08a'); p.set(lx + 1, y, IRON.vdk); }
  }
  return p.done();
}

/* ================= The billboard: a poster hoarding hung on the far warehouse, under its cornice =================
   A steel frame painted the footbridge's green, bolted to the brick on stand-off brackets, a grated catwalk under
   it and three floodlamps on short arms below the catwalk, shining up at the face (BB). */
const BBC = { x: BB.x - 8, y: BB.y - 6, w: BB.w + 16, h: BB.h + 34 };   // the structure's own canvas
const BB_LIT = { hi: '#b0bc92', mid: '#7e9474' };                       // the green where the lamps light it
function renderBillboard() {
  const p = painter(BBC.w, BBC.h), set = (i, y, col) => p.set(i - BBC.x, y - BBC.y, col);
  const hl = (x0, y, x1, col) => { for (let i = x0; i <= x1; i++) set(i, y, col); };
  const vl = (x, y0, y1, col) => { for (let y = y0; y <= y1; y++) set(x, y, col); };
  const L = BB.x - 2, R = BB.x + BB.w + 1, T = BB.y - 1, B = BB.y + BB.h + 1, CW = B + 1;
  for (const y of [BB.y + 3, BB.y + BB.h - 4]) { set(L - 1, y, IRON.vdk); set(R + 1, y, IRON.vdk); set(R + 2, y, '#2a1612'); }   // stand-offs
  // the frame: a lit bead along the bottom rail, the right-hand rail in shadow
  hl(L, T, R, IRON.mid); set(L, T, IRON.dk); set(R, T, IRON.vdk);
  vl(L, T + 1, B, IRON.dk); vl(L + 1, T + 1, B - 2, IRON.hi);
  vl(R - 1, T + 1, B - 2, IRON.mid); vl(R, T + 1, B, IRON.vdk);
  hl(L + 1, B - 1, R - 1, BB_LIT.hi); hl(L + 1, B, R - 1, IRON.mid);
  for (let y = BB.y; y < BB.y + BB.h; y++) hl(BB.x, y, BB.x + BB.w - 1, '#0d0f24');   // (the face is painted per frame)
  // the catwalk: grating, stringer, brackets back to the wall
  hl(L - 2, CW, R + 2, IRON.mid); for (let i = L - 2; i <= R + 2; i += 2) set(i, CW, BB_LIT.mid);
  hl(L - 2, CW + 1, R + 2, IRON.dk); hl(L - 2, CW + 2, R + 2, IRON.vdk);
  for (const bx of [L, BB.x + 14, BB.x + 30, R]) { vl(bx, CW + 3, CW + 5, IRON.vdk); set(bx + (bx < BB_LAMPS[1] ? 1 : -1), CW + 3, IRON.vdk); }
  // floodlamps: an arm down from the catwalk, the housing, its lit lens facing up at the face
  for (const lx of BB_LAMPS) {
    vl(lx, CW + 3, BB_LAMP_Y - 1, IRON.vdk);
    hl(lx - 2, BB_LAMP_Y, lx + 2, '#ffe2a0'); hl(lx - 1, BB_LAMP_Y, lx + 1, '#fff6d8');
    hl(lx - 2, BB_LAMP_Y + 1, lx + 2, IRON.vdk); set(lx - 2, BB_LAMP_Y + 1, IRON.dk);
    hl(lx - 1, BB_LAMP_Y + 2, lx + 1, IRON.vdk);
  }
  return p.done();
}
/* the city's own ad: FISCH BRÖTCHEN, a herring in a roll, lit in fans from the lamps below */
const POSTER_PAL = ['#8a7658', '#a8936e', '#c6b28a', '#e0cfa6', '#f2e6c4', '#fcf6e2'];
const BAND_PAL = ['#121a38', '#18244c', '#1f2e60', '#283c78', '#344c90', '#4a64a8', '#6a84c0'];
const RULE_PAL = ['#8a1a20', '#b0242a', '#d0343a'];
const BIG = {                                         // chunky poster letters, 2 px strokes
  F: ['11111', '11111', '11...', '1111.', '1111.', '11...', '11...', '11...'],
  I: ['11', '11', '11', '11', '11', '11', '11', '11'],
  S: ['.1111', '11111', '11...', '1111.', '.1111', '...11', '11111', '1111.'],
  C: ['.1111', '11111', '11...', '11...', '11...', '11...', '11111', '.1111'],
  H: ['11.11', '11.11', '11.11', '11111', '11111', '11.11', '11.11', '11.11'],
};
const ROLL = { x: 1, y: 1, rows: [                    // a herring in a split roll; its tail is drawn per frame (it flips)
  '...hhhhhhhhh...', '..hCCuuuuuCCd..', '..CCCCCCCCCCd..', '..LlLlLlLlLlL..', '..FFFFFFFFFFkk.',
  '..SSSSSSSSSSSen', '..ppppppppppKK.', '..oOoOoOoOoOo..', '..bBBBBBBBBBd..'] };
const ROLL_COL = { h: '#eab466', C: '#c47832', d: '#8a4a1e', L: '#7cc048', l: '#4a8a2e', F: '#4e6284', S: '#e4eaf2',
  u: '#f4dca4', p: '#aab6c8', e: '#141824', n: '#8a98ae', k: '#b8c4d4', K: '#7a8aa2', o: '#a8487e', O: '#f2eaf2', B: '#c8843a', b: '#8a4a1e' };
const TAIL = '#6a7890', RULE = 10;
function renderPoster(dim) {                          // dim: the right-hand lamp is out
  const { w, h } = BB, p = painter(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    let cone = 0;
    BB_LAMPS.forEach((lx, k) => {
      const up = h - 1 - j, dx = (i - (lx - BB.x)) / (4 + up * 0.9);
      cone = Math.max(cone, (k === 2 && dim ? 0.2 : 1) * Math.exp(-dx * dx) * Math.max(0, 1 - up / 17));
    });
    let v = (j > RULE ? 0.3 : 0.4) + 0.16 * (j / h) + 0.2 * smooth(0.15, 0.3, cone) + 0.24 * smooth(0.5, 0.65, cone);
    if (dim) v -= 0.2 * Math.exp(-(((i - (BB_LAMPS[2] - BB.x)) / 10) ** 2)) * (0.4 + 0.6 * j / h);
    p.set(i, j, dpick(j === RULE ? RULE_PAL : j > RULE ? BAND_PAL : POSTER_PAL, v, i, j));
  }
  ROLL.rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (ROLL_COL[row[i]]) p.set(ROLL.x + i, ROLL.y + j, ROLL_COL[row[i]]); });
  let cx = 17;
  for (const ch of 'FISCH') {
    const g = BIG[ch];
    for (const [dx, dy, col] of [[1, 1, '#6a1a1e'], [0, 0, '#c8262c']])
      g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.set(cx + i + dx, 1 + j + dy, j === g.length - 1 && !dx ? '#e65448' : col); });
    cx += g[0].length + 1;
  }
  const c = p.done();
  text(c.getContext('2d'), 'BRÖTCHEN', (w - textWidth('BRÖTCHEN') + 1) >> 1, RULE + 2, '#f6ecd0');
  return c;
}

/* ================= Water ================= */
function renderWater() {
  const h = QUAY - HOR, P = ['#24333e', '#1b2934', '#15222c', '#101a24', '#0c141d', '#091019'];
  return painterFill(W, h, (i, y) => dpick(P, Math.pow(y / h, 0.75), i, y));
}
function painterFill(w, h, fn) {
  const p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) { const col = fn(i, y); if (col) p.set(i, y, col); }
  return p.done();
}

/* ================= A huge container ship (bow right): boxes six high, bridge forward, funnel aft ================= */
const SHIP_W = 152, SHIP_H = 34;
function renderShip() {
  const p = painter(SHIP_W, SHIP_H), r = rng(303);
  for (let y = 25; y < SHIP_H; y++) {                                  // hull, raked bow, red boot-top at the waterline
    const x0 = 1 + Math.max(0, y - 29), x1 = SHIP_W - 1 - Math.round((y - 25) * 1.2);
    for (let i = x0; i <= x1; i++) {
      let col = y === 25 ? '#46526a' : y >= 32 ? '#6e2626' : '#182032';
      if (y === 26) col = '#222c40';
      if (y === 28 && i % 12 === 5 && i < x1 - 4) col = '#28324a';
      p.set(i, y, col);
    }
  }
  for (let i = 3; i < SHIP_W - 3; i += 3) p.set(i, 24, '#4e5a6e');      // deck rail
  for (let x = 8; x < 140; x += 5) {                                   // container bays
    if ((x >= 93 && x < 111) || (x >= 16 && x < 28)) continue;
    const tiers = x > 128 ? 3 + Math.floor(r() * 2) : 4 + Math.floor(r() * 3);
    for (let k = 0; k < tiers; k++) {
      const [b, tp] = CONT[Math.floor(r() * CONT.length)], y = 22 - k * 2;
      p.rect(x, y, 4, 1, k === tiers - 1 ? tp : b); p.rect(x, y + 1, 4, 1, b);
      if (r() < 0.25) p.set(x + Math.floor(r() * 4), y + 1, '#141820');
    }
  }
  for (let y = 4; y < 25; y++) for (let i = 96; i <= 107; i++)          // bridge superstructure
    p.set(i, y, i === 96 ? '#f0f2f6' : i >= 106 ? '#8a90a0' : y % 4 === 2 ? '#b8bec8' : '#d6dae2');
  for (const wy of [6, 10, 14, 18, 22]) for (let i = 97; i < 106; i++) p.set(i, wy, (i & 1) ? (r() < 0.55 ? '#ffd48a' : '#384050') : '#aab0bc');
  for (let i = 97; i < 106; i++) p.set(i, 6, i % 2 ? '#9ad0ff' : '#5a7898');   // wheelhouse windows
  p.rect(92, 4, 20, 1, '#e4e8ee'); p.rect(92, 5, 20, 1, '#8a90a0');     // bridge wings
  for (let y = 0; y < 4; y++) p.set(101, y, '#8a8e98');
  p.rect(99, 2, 5, 1, '#aab0ba');                                       // radar mast
  for (let y = 8; y < 25; y++) for (let i = 18; i <= 25; i++)           // funnel with a white band
    p.set(i, y, y < 10 ? '#16181e' : (y === 12 || y === 13) ? (i < 24 ? '#e8ecf0' : '#a8acb4') : i < 20 ? '#565c68' : i > 23 ? '#2a2e38' : '#3e434e');
  for (let y = 18; y < 25; y++) p.set(141, y, '#6a7282');               // foremast
  return p.done();
}
/* harbour ferry (bow left): the HADAG red-orange hull, white saloon with warm windows, wheelhouse on top */
const FERRY_HULL = ['#e8ece8', '#b4462e', '#943624', '#7a2c22', '#141216'];   // strake, hull lit to shadow, waterline
function renderFerry() {
  const w = 48, h = 17, p = painter(w, h);
  for (let y = 12; y < h; y++) {
    const x0 = Math.max(0, 4 - (y - 12)) + (y > 14 ? 1 : 0), x1 = w - 2;
    for (let i = x0; i <= x1; i++) p.set(i, y, FERRY_HULL[y - 12]);
  }
  for (let y = 6; y < 12; y++) for (let i = 6; i <= 42; i++) {
    let col = y === 11 ? '#aab2b4' : '#e2e6e4';
    if ((y === 7 || y === 8 || y === 9) && i > 7 && i < 41) col = (i - 8) % 4 === 3 ? '#c8ceca' : y === 7 ? '#ffe2a0' : '#f4b868';
    if (i === 6) col = '#ffffff'; if (i === 42) col = '#9aa2a4';
    p.set(i, y, col);
  }
  for (let i = 8; i <= 40; i += 2) p.set(i, 5, '#aab0b4');
  for (let y = 2; y < 6; y++) for (let i = 24; i <= 33; i++) p.set(i, y, y === 2 ? '#f4f6f4' : (y === 3 && i > 24 && i < 33) ? '#2a3440' : '#dfe4e2');
  p.set(26, 3, '#ffd490');
  for (let y = 0; y < 2; y++) p.set(31, y, '#5a6068');
  p.set(1, 11, '#ff4040');
  return p.done();
}
/* Barkasse (bow right): low varnished launch, canopy on posts, lanterns and passengers */
function renderBarkasse() {
  const w = 38, h = 11, p = painter(w, h);
  for (let y = 5; y < h; y++) {
    const x0 = 1 + Math.max(0, y - 8), x1 = w - 2 - Math.max(0, y - 6) * 2;
    for (let i = x0; i <= x1; i++) {
      if (y === 5 && i < 32) continue;
      p.set(i, y, y === 5 || y === 6 ? (i % 5 === 0 ? '#7a4e2a' : '#b07a42') : y === h - 1 ? '#1e120c' : (i + y) % 7 === 0 ? '#4a2a18' : '#5e3620');
    }
  }
  for (let i = 5; i <= 31; i++) { p.set(i, 1, '#ece4d0'); p.set(i, 2, '#b8ae98'); }
  for (let i = 8; i <= 30; i += 6) p.set(i, 3, '#fff0c0');
  for (const px_ of [6, 18, 30]) for (let y = 3; y < 6; y++) p.set(px_, y, '#3a2a20');
  for (let i = 7; i < 30; i++) if (i !== 18) { p.set(i, 3, (i % 3) ? '#e8b070' : '#c08050'); p.set(i, 4, (i % 4 === 1) ? '#2a1a16' : '#9a6038'); }
  p.set(2, 3, '#1a1414'); p.set(2, 4, '#1a1414'); p.set(3, 4, '#1a1414'); p.set(2, 2, '#e0c0a0');   // helmsman
  for (let y = 0; y < 5; y++) p.set(35, y, '#6a5a4a');
  return p.done();
}
/* gulls: near (15 px), mid and far, three flap frames each */
const GULL_KEY = { w: '#eef2f8', g: '#9aa6b8', k: '#2a2e38', y: '#e8b830', s: '#c4cad4' };
const GULL_ROWS = [
  [['x...x', '.x.x.', '..x..'], ['.x.x.', 'x.x.x'], ['..x..', '.x.x.', 'x...x']],
  [['k.......k', '.g.....g.', '..ww.ww..', '....w....'], ['.gw...wg.', 'k..www..k'], ['...www...', '.ww...ww.', 'k.......k']],
  [['k.............k', '.g...........g.', '.gw.........wg.', '..gw.......wg..', '...ww.....ww...', '....wwwwwwwwwy.', '......ssss.....'],
    ['...............', 'kgg.........ggk', '..ggww...wwgg..', '....wwwwwwwwwy.', '......ssss.....'],
    ['....wwwwwwwwwy.', '...ww.ssss.ww..', '..gw.......wg..', '.gg.........gg.', 'k.............k']],
];
function makeGulls() {
  return GULL_ROWS.map((frames, size) => frames.map(rows => {
    const key = size === 0 ? { x: '#c8d0dc' } : GULL_KEY;
    return [sprite(rows, key), sprite(rows, key, true)];
  }));
}
const PERCH_ROWS = ['..ww.', '.wwwy', 'gww..', 'ggw..', '.y...'], PERCH_KEY = { w: '#e8ecf2', g: '#8a96a8', y: '#d8a830' };
/* tuxedo cat sitting on a bollard, facing the water; eyes glint, tail drawn per frame */
const CAT_ROWS = ['.r...r...', '.gr.rg...', '.ggggg...', 'geggggh..', 'wngggghh.', '.wwggggh.', '..wggggh.', '..wgdgdgh', '.wwggdggh', '.wwgdggdh', '.wwgggggh', '..wwggggh'];
const CAT_KEY = { g: '#6e707c', d: '#44464f', h: '#a2acbc', r: '#b4bece', w: '#e2e4ea', e: '#b8e070', n: '#e8a0a8' };
/* hanging quay lantern */
/* someone in a yellow oilskin, hood up, leaning into the wind (legs drawn per frame) */
const WALKER_ROWS = ['..hhh.......', '.hYYYh......', 'hYYYYYd.....', 'fsYYYYd.....', 'fsYYYYd.....', '.fYYYd......', '.hYYYYd.....',
  '.hYYYYYd....', '..hYYYYd....', '..hYYYYYd...', '..hYYYYYd...', '..bbbbbbbb..', '...hYYYYYd..', '...hYYYYYdd.', '...hYYYYYYdd',
  '...hYYYYYYYd', '....hYYYYYYd', '....ddddddd.'];
const WALKER_KEY = { h: '#f4d060', Y: '#d8a824', d: '#98701a', f: '#7a5a14', s: '#d8a888', b: '#b08a1e' };
const LANTERN_ROWS = ['..kkk..', '.kkkkk.', 'kkkkkkk', '.kLLLk.', '.kLHLk.', '.kLHLk.', '.kLLLk.', '.kLLLk.', '..kkk..', '...k...'];
const LANTERN_KEY = { k: '#15181a', L: '#ffd690', H: '#fff4d8' };
/* Hamburg's flag: red, with a white castle: a taller domed centre tower with its cross between two side towers,
   over a wall that flares out at the base, a dark arched gate in the middle (columns 2-12 wave as one) */
const FLAG_ROWS = ['RRRRRRRRRRRRRRRR', 'RRRRRRRWRRRRRRRR', 'RRRRRRWWWRRRRRRR', 'RRRWWRWWWRWWRRRR', 'RRRWWRWWWRWWRRRR', 'RRRWWRWWWRWWRRRR',
  'RRRWWWWWWWWWRRRR', 'RRWWWWWDWWWWWRRR', 'RRWWWWDDDWWWWRRR', 'RRRRRRRRRRRRRRRR'];

/* ================= Our quay: granite curb, wet cobbles, bollards, a lifebuoy, the lamp post ================= */
const QUAY_SPOTS = [
  { x: LAMP.arm, y: 258, r: 52, col: '#6a5038' }, { x: EP_CX, y: 258, r: 70, col: '#2e3e54' }, { x: 440, y: 258, r: 44, col: '#5a3a2c' },
];
const BUOY = { x: 96, y: 232 };
function renderQuay() {
  const p = painter(), r = rng(31);
  for (let i = 0; i < W; i++) {
    p.set(i, QUAY, r() < 0.12 ? '#a8aeb8' : '#848a96'); p.set(i, QUAY + 1, '#5e646e');
    for (let y = QUAY + 2; y < QUAY + 5; y++) p.set(i, y, (i + 7) % 23 === 0 ? '#1c1e24' : y === QUAY + 2 ? '#454a54' : '#363a44');
    p.set(i, QUAY + 5, '#15171d');
  }
  const rows = [[QUAY + 6, 2], [QUAY + 8, 3], [QUAY + 11, 3], [QUAY + 14, 4], [QUAY + 18, 4], [QUAY + 22, 5], [QUAY + 27, 5]];
  rows.forEach(([y0, rh], ri) => {
    let cx = -Math.floor(r() * 6);
    const sw = 4 + ri;
    while (cx < W) {
      const w = sw + Math.floor(r() * 3);
      for (let y = y0; y < y0 + rh - (rh > 2 ? 1 : 0); y++) for (let i = cx + 1; i < cx + w; i++) {
        if (i < 0 || i >= W || y >= H) continue;
        if ((i === cx + 1 || i === cx + w - 1) && (y === y0 || y === y0 + rh - 2) && rh > 2) continue;
        let col = ['#20232b', '#252831', '#1d2027'][Math.floor(hash(i >> 2, y0, 7) * 3)];
        if (y === y0) col = '#353a46';
        for (const s of QUAY_SPOTS) {
          const dd = Math.hypot((i - s.x) / s.r, (y - s.y) / (s.r * 0.35));
          if (dd < 1 && y <= y0 + 1 && bayer(i, y) < (1 - dd) * 1.3) col = s.col;
        }
        p.set(i, y, col);
      }
      cx += w;
    }
  });
  for (let y = QUAY + 6; y < H; y++) for (let i = 0; i < W; i++) if (!p.has(i, y)) p.set(i, y, '#101217');
  // cast-iron bollards on the curb
  for (const bx of BOLLARDS) {
    const lit = bx < 200 ? '#6a5a4c' : '#56647a';
    for (let y = 230; y <= QUAY + 5; y++) {
      const hw = y <= 232 ? 4 : y <= 234 ? 2 : y >= QUAY + 4 ? 4 : 3;
      for (let dx = -hw; dx <= hw; dx++) {
        let col = '#15161c';
        if (dx === -hw) col = '#353944'; else if (dx === -hw + 1 && hw > 2) col = '#262a33'; else if (dx === hw) col = '#0b0c10';
        if (y === 230) col = dx < hw - 1 ? lit : '#2a2e38';
        p.set(bx + dx, y, col);
      }
    }
  }
  // lifebuoy on its post
  for (let y = BUOY.y - 6; y < QUAY + 4; y++) { p.set(BUOY.x + 6, y, '#2a3a30'); p.set(BUOY.x + 7, y, '#16201a'); }
  p.rect(BUOY.x + 3, BUOY.y - 6, 5, 1, '#2a3a30');
  for (let y = -6; y <= 6; y++) for (let x = -6; x <= 6; x++) {
    const d = Math.hypot(x, y);
    if (d > 6.1 || d < 3) continue;
    const q = Math.floor(((Math.atan2(y, x) + TAU + 0.4) % TAU) / (TAU / 4));
    let col = q % 2 ? '#e8e6e0' : '#d02a2c';
    if (x + y > 3) col = q % 2 ? '#a8a6a8' : '#8a1a20';
    if (d > 5.4 && x + y < -2) col = q % 2 ? '#ffffff' : '#f05048';
    p.set(BUOY.x + x, BUOY.y + y, col);
  }
  // the lamp post: plinth, fluted shaft, collars, swan-neck arm
  const I = '#141a18', IH = '#34443e', IW = '#6a5a3e';
  for (let y = QUAY - 4; y <= QUAY + 5; y++) { const wd = y > QUAY + 2 ? 9 : y > QUAY - 1 ? 7 : 5; p.rect(LAMP.x - (wd >> 1), y, wd, 1, I); p.set(LAMP.x - (wd >> 1), y, IH); }
  for (let y = LAMP.top + 6; y < QUAY - 4; y++) { p.rect(LAMP.x - 1, y, 3, 1, I); p.set(LAMP.x - 1, y, y < 190 ? IW : IH); }
  for (const y of [QUAY - 8, 200, 162, LAMP.top + 12]) { p.rect(LAMP.x - 2, y, 5, 2, I); p.set(LAMP.x - 2, y, IW); }
  for (let y = LAMP.top; y < LAMP.top + 6; y++) p.set(LAMP.x, y, I);
  p.set(LAMP.x, LAMP.top - 1, IW);
  for (let k = 0; k <= LAMP.arm - LAMP.x; k++) {
    const u = k / (LAMP.arm - LAMP.x), y = Math.round(LAMP.top + 7 - 4 * Math.sin(Math.PI * Math.min(1, u * 0.9 + 0.1)) + u * 1.5);
    p.set(LAMP.x + k, y, I); p.set(LAMP.x + k, y - 1, k % 3 ? IH : I);
  }
  for (let k = 0; k < 7; k++) p.set(LAMP.x + 2 + k, LAMP.top + 14 - k, I);
  for (let y = LAMP.top + 5; y < LAMP.top + 9; y++) p.set(LAMP.arm, y, I);
  return p.done();
}

/* ================= Wind-driven rain (3 depths), sheets, rings ================= */
const RAIN_LIGHTS = [
  { x: LAMP.arm, y: LAMP.top + 16, r: 48, w: 1, near: true }, ...BRG_LAMPS.map(x => ({ x, y: BRG.deck - 22, r: 16, w: 1 })),
  { x: 440, y: 150, r: 60, w: 1 }, { x: 316, y: 170, r: 30, w: 1 }, { x: EP_CX, y: 104, r: 56, w: 2 },
].map(l => ({ ...l, r2: l.r * l.r }));
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB_LAMP_Y - 4, r: 28, w: 1, r2: 28 * 28 }];   // + the billboard's lamps
// a picked weather (site/js/engine/weather.js): Hamburg draws its own rain, with its sheets and the rings on the basin and
// the quay puddles; snow and leaves land on the far terminal, the basin below the warehouse walls and past the bottom
// of the frame, and catch the same lights as the rain (the Elbphilharmonie's glass cool, the lamps warm)
const WEATHER = weatherPlans(['rain']);
const WX_LAND = [[150, HOR], [NB.base + 2, QUAY - 2], [H + 4, H + 4]];
const wxLights = list => list.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: l.w === 2 ? '#d2e8ff' : '#ffd6a0' }));
const WX = { land: WX_LAND, lights: wxLights(RAIN_LIGHTS) };
const WX_BB = { land: WX_LAND, lights: wxLights(RAIN_LIGHTS_BB) };
function litAt(x, y, far, lights) {
  for (const l of lights) { if (far && l.near) continue; const dx = x - l.x, dy = y - l.y; if (dx * dx + dy * dy < l.r2) return l.w; }
  return 0;
}
function nearLand(x, a, b) {
  for (const bx of BOLLARDS) if (Math.abs(x - bx) <= 4 && a < 0.4) return 229;
  if (a < 0.12) return QUAY;
  return QUAY + 7 + Math.floor(b * (H - QUAY - 9));
}
const RAIN = [
  { count: 190, nMin: 150, nMax: 180, fall: 190, ff: 1, len: 3, far: true, s0: 0.28, s1: 0.42, col: 'rgba(150,172,198,0.3)', warm: 'rgba(255,210,150,0.5)', cool: 'rgba(200,226,255,0.55)', splash: '#8aa0b8',
    land: (x, a) => 150 + Math.floor(a * 38) },
  { count: 150, nMin: 200, nMax: 230, fall: 240, ff: 0.9, len: 5, s0: 0.32, s1: 0.52, col: 'rgba(170,192,216,0.4)', warm: 'rgba(255,214,160,0.62)', cool: 'rgba(210,232,255,0.62)', splash: '#a8bcd0',
    land: (x, a) => 194 + Math.floor(a * (QUAY - 196)) },
  { count: 72, nMin: 260, nMax: 290, fall: 300, ff: 0.84, len: 8, s0: 0.38, s1: 0.6, col: 'rgba(196,214,236,0.5)', warm: 'rgba(255,220,170,0.78)', cool: 'rgba(220,238,255,0.7)', splash: '#c8d8e8',
    land: nearLand },
];
RAIN.forEach((L, li) => {
  const r = rng(200 + li);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
const RINGS = [], PUDDLE_RINGS = [];
{ const r = rng(71); for (let i = 0; i < 46; i++) RINGS.push({ id: i, n: 100 + Math.floor(r() * 50), ph: r() }); }
{ const r = rng(72); for (let i = 0; i < 16; i++) PUDDLE_RINGS.push({ id: 60 + i, n: 80 + Math.floor(r() * 30), ph: r() }); }
/* a tileable sheet of faint diagonal streaks, banded into veils; scrolled with the wind */
function renderSheet(seed) {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data, r = rng(seed), col = hex('#c4d4e6');
  for (let n = 0; n < 1100; n++) {
    const x0 = r() * W, y0 = r() * H, len = 6 + Math.floor(r() * 12);
    const band = 0.5 + 0.5 * Math.sin(TAU * (2 * x0 / W + y0 / H) + seed) * Math.sin(TAU * (3 * x0 / W - 2 * y0 / H));
    const a = (0.1 + 0.9 * band * band) * (0.35 + 0.65 * r());
    for (let k = 0; k < len; k++) {
      const X = ((Math.round(x0 + k * 0.62) % W) + W) % W, Y = ((Math.round(y0 + k) % H) + H) % H, o = (Y * W + X) * 4;
      d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = Math.max(d[o + 3], Math.round(a * 110));
    }
  }
  x.putImageData(img, 0, 0);
  return c;
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 4; d[o + 1] = 8; d[o + 2] = 14; d[o + 3] = Math.round(q * 0.45 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Timings (loop seconds) and small tables ================= */
const SHIP = { t0: 18, t1: 168, x0: -2, x1: 446 };   // bow x glides x0 -> x1, passing behind the Elbphilharmonie
const HORN_T = 40;                                   // the ship blows its horn as its bridge comes into view
const FERRY = { t0: 114, t1: 174, wl: 214, x0: 486, x1: -54 };
const BARK = { t0: 58, t1: 100, wl: 230, x0: -42, x1: 486 };
const SEAL = { t0: 186, t1: 205, x: 112, wl: 223 };
const NEAR_GULLS = [{ a: 44, b: 51, y0: 40, dip: 22 }, { a: 138, b: 144, y0: 58, dip: 16 }, { a: 211, b: 218, y0: 34, dip: 26 }];
const GULL_T = NEAR_GULLS.map(g => +((g.a + g.b) / 2).toFixed(1));
const BELLS = [148, 151.5, 155];
// people (see "People" below); the couple at the quay edge stays all loop
const WALKER = { t0: 124, t1: 172, fy: 253 };        // the walker in the oilskin, into the wind (right to left)
const BIKE = { t0: 192, t1: 230, fy: 252, x0: -14, x1: 502 };   // pushing a bike along the quay, with the wind
const JOGS = [{ t0: 22, dir: 1 }, { t0: 196, dir: -1 }], JOG_DUR = 5.6;   // a jogger over the footbridge, there and back
const DOGW = { t0: 64, stop: 69, go: 76, t1: 84, x: 374 };   // a dog walker crosses it; the dog stops under the gull
const PLANE = { t0: 62, t1: 118 };
const LAMP_FLICK = [[76, 77.4], [183, 184.6]];
const BRG_FLICK = [[33, 34.2], [131.5, 132.3]];
const DALBEN = [{ x: 58, wl: 205, n: 4, seed: 5 }, { x: 138, wl: 211, n: 3, seed: 6 }];
const EAR_FLICKS = [21.5, 63.2, 109.8, 157.3, 201.4, 229.6];
const FAR_GULLS = [], MID_GULLS = [];
{
  const r = rng(515);
  for (let i = 0; i < 6; i++) FAR_GULLS.push({ cx: 30 + r() * 130, cy: 84 + r() * 36, rx: 10 + r() * 26, ry: 3 + r() * 7,
    k: (3 + Math.floor(r() * 4)) * (r() < 0.5 ? 1 : -1), ph: r(), kf: 20 + Math.floor(r() * 30), pf: r() });
  for (let i = 0; i < 3; i++) MID_GULLS.push({ cx: 70 + i * 110 + r() * 30, cy: 150 + r() * 20, rx: 6 + r() * 8, ry: 3 + r() * 4,
    k: 3 + Math.floor(r() * 4), ph: r(), kf: 30 + Math.floor(r() * 20), pf: r() });
}
const waterlineAt = x => x < EP.x0 - 2 ? HOR : x <= EP.x1 + 2 ? EP.wl : x < FB.x0 ? HOR : x < NB.x0 ? FB.base : NB.base;
const WATER_RECTS = [[0, HOR, EP.x0 - 2], [EP.x0 - 2, EP.wl, EP.x1 + 3], [EP.x1 + 3, HOR, FB.x0], [FB.x0, FB.base, NB.x0], [NB.x0, NB.base, W]];
const STREAKS = [
  { x: EP_CX - 8, w: 16, col: '#9ec4ec', a: 0.2, seed: 1, y0: EP.wl }, { x: EP_CX + 34, w: 8, col: '#b8d4f2', a: 0.16, seed: 2, y0: EP.wl },
  ...EP_LAMPS.map((x, i) => ({ x, w: 2, col: '#ffe0a0', a: 0.34, seed: 3 + i, y0: EP.wl })),
  ...FB_SPOTS.map((x, i) => ({ x, w: 4, col: '#ffb070', a: 0.3, seed: 10 + i, y0: FB.base })),
  ...NB_SPOTS.map((x, i) => ({ x, w: 6, col: '#ffa860', a: 0.34, seed: 14 + i, y0: NB.base })),
  ...BRG_LAMPS.map((x, i) => ({ x, w: 3, col: '#ffd890', a: 0.4, seed: 20 + i, y0: FB.base })),
  { x: LAMP.arm, w: 5, col: '#ffc878', a: 0.3, seed: 24, y0: 200 },
  ...MASTS.map((x, i) => ({ x, w: 2, col: '#ffc27a', a: 0.26, seed: 30 + i, y0: HOR + 1 })),
];

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR_C, HAZE_C, EPH, SHEEN_C, SHEEN_S, NEAR, FARB, GAP, BRIDGE_C, WATER_C, SHIP_C,
  FERRY_C, BARK_C, GULL_SPR, CAT_C, SEAL_C, SEAL_F, LANTERN_C, QUAY_C, SHEET_C, VIG_C, DALB_C, PERCH_GULL, GULL_PERCH_F, WALKER_C,
  WALKER_VF, BB_C, POSTERS, PITCH;
function renderDalben(n, seed) {
  const r = rng(seed), w = n * 3, h = 20, p = painter(w, h), mid = n >> 1;
  for (let k = 0; k < n; k++) {
    const x = k * 3, top = 4 + Math.floor(r() * 3) - (k === mid ? 3 : 0);
    for (let y = top; y < h; y++) { p.set(x, y, '#6a5040'); p.set(x + 1, y, '#2e241c'); if (k < n - 1) p.set(x + 2, y, '#1a1612'); }
    p.set(x, top, '#8a7462'); p.set(x + 1, top, '#5a4a3e');
  }
  for (const by of [9, 15]) for (let i = 0; i < w - 1; i++) p.set(i, by, i === 0 ? '#9a9aa0' : '#5a5a62');
  for (let y = h - 3; y < h; y++) for (let i = 0; i < w - 1; i++) if (p.has(i, y)) p.set(i, y, y === h - 3 ? '#3a5a44' : '#23382c');   // weed at the waterline
  return { c: p.done(), top: 1 + mid * 3 };
}
function prerender() {
  HL = {
    moon: makeHalo(40, '#9ab0c8', 0.2, 1.8), moonCore: makeHalo(12, '#dfe8f0', 0.28, 1.5),
    port: makeHalo(96, '#ffae60', 0.1, 1.6), mast: makeHalo(16, '#ffc27a', 0.3, 1.5), red: makeHalo(4, '#ff3030', 0.65, 1),
    epGlass: makeHalo(86, '#78b8ff', 0.15, 1.6), epPlaza: makeHalo(28, '#ffc070', 0.16, 1.6), epLamp: makeHalo(6, '#ffe0a0', 0.45),
    win: makeHalo(8, '#ffb45a', 0.2), flood: makeHalo(40, '#ffa458', 0.2, 1.6), floodS: makeHalo(24, '#ffa458', 0.16),
    blamp: makeHalo(14, '#ffd490', 0.38), blampCore: makeHalo(5, '#fff0c8', 0.55, 1.2),
    lamp: makeHalo(60, '#ffb866', 0.26, 1.7), lampCore: makeHalo(16, '#ffe6b0', 0.5, 1.4),
    michel: makeHalo(24, '#ffe0a0', 0.16, 1.5), clock: makeHalo(8, '#fff4d0', 0.4, 1.3), bell: makeHalo(40, '#ffe0a0', 0.2, 1.5),
    ship: makeHalo(22, '#ffd8a0', 0.18), white: makeHalo(4, '#ffffff', 0.6, 1), green: makeHalo(4, '#40ff80', 0.5, 1),
    ferry: makeHalo(24, '#ffd8a0', 0.2), boat: makeHalo(12, '#ffc060', 0.34), bike: makeHalo(9, '#ffecc8', 0.42, 1.3),
    bbGlow: makeHalo(30, '#ffb060', 0.16, 1.6), bbLamp: makeHalo(7, '#ffe0a0', 0.5, 1.3),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR_C = renderFar(); HAZE_C = renderHaze();
  EPH = renderElbphi(); SHEEN_C = renderSheen(); SHEEN_S = makeCanvas(EP.x1 - EP.x0 + 1, EP.glass + 1);
  NEAR = renderNear(); FARB = renderFarBlock(); GAP = renderGap(); BRIDGE_C = renderBridge();
  BB_C = renderBillboard(); POSTERS = [renderPoster(false), renderPoster(true)];
  PITCH = makePitch(BB.w, BB.h, { accent: '#4fc3f7' });
  WATER_C = renderWater(); SHIP_C = renderShip();
  FERRY_C = renderFerry();
  BARK_C = renderBarkasse(); GULL_SPR = makeGulls();
  CAT_C = sprite(CAT_ROWS, CAT_KEY);
  SEAL_C = sprite(['..ddd..', '.dgggd.', 'dkggggd', 'nggggdd', '.wgggd.'], { d: '#3e3e46', g: '#6a6a72', k: '#0c0c10', n: '#9a9aa2', w: '#c8c8d0' });
  SEAL_F = flipCanvas(SEAL_C);
  PERCH_GULL = sprite(PERCH_ROWS, PERCH_KEY); GULL_PERCH_F = flipCanvas(PERCH_GULL);
  LANTERN_C = sprite(LANTERN_ROWS, LANTERN_KEY); WALKER_C = sprite(WALKER_ROWS, WALKER_KEY); WALKER_VF = sprite([...WALKER_ROWS].reverse(), WALKER_KEY);
  QUAY_C = renderQuay(); SHEET_C = renderSheet(9); VIG_C = renderVignette(); bakeFolk();
  DALB_C = DALBEN.map(d => renderDalben(d.n, d.seed));
}

/* ================= Per-frame: sky and the far bank ================= */
const winOn = (w, salt, t) => hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p;
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#d8e4f0';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.85 + 0.15 * wave(t, 4));
  halo(HL.moonCore, MOON.x, MOON.y, 0.9);
  ctx.globalAlpha = 0.85; ctx.drawImage(MOON_C, MOON.x - 6, MOON.y - 6); ctx.globalAlpha = 1;
  CLOUDS.forEach((L, i) => {
    const off = scroll(t, L.k, W);
    ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y);
    if (i === 0) drawPlane(t);
  });
}
/* a late arrival letting down toward the airport, between the cloud decks */
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(492 - u * 510), y = Math.round(36 + u * 22);
  rect(ctx, x, y, 4, 1, '#2a3440'); px(ctx, x + 2, y - 1, '#2a3440');
  halo(HL.white, x - 1, y, 0.7); px(ctx, x - 1, y, '#fff8e8');
  if (frac(t / 1.2) < 0.1) { px(ctx, x + 2, y, '#ffffff'); halo(HL.white, x + 2, y, 1); }
  if (frac(t / 1.5 + 0.3) < 0.5) { px(ctx, x + 3, y - 1, '#ff3a3a'); halo(HL.red, x + 3, y - 1, 0.6); }
}
function gullFrame(t, g) { return wave(t, g.kf, g.pf) > 0.3 ? [0, 1, 2, 1][step(t, 1200) % 4] : 1; }
function drawFarGulls(t) {
  for (const g of FAR_GULLS) {
    const a = TAU * (g.k * t / LOOP + g.ph), x = g.cx + g.rx * Math.cos(a), y = g.cy + g.ry * Math.sin(a);
    ctx.drawImage(GULL_SPR[0][gullFrame(t, g)][0], Math.round(x) - 2, Math.round(y) - 1);
  }
}
function craneRed(x, y, on) { px(ctx, x, y, on ? '#ff4a3a' : '#5a1a18'); if (on) halo(HL.red, x, y, 0.8); }
function drawFar(t) {
  halo(HL.port, 80, 150, 0.95);
  ctx.drawImage(FAR_C, 0, 0);
  for (const cr of CRANES) {
    const c = cr.c;
    if (cr.type === 'flat') {                                          // trolley shuttling, spreader rising and falling
      const tx = Math.round(lerp(c - 26, c + 4, 0.5 + 0.5 * wave(t, cr.k, cr.ph)));
      const sy = Math.round(CB + 7 + 14 * (0.5 + 0.5 * wave(t, cr.kh, cr.ph2)));
      rect(ctx, tx, CB + 3, 5, 2, '#3a4652'); px(ctx, tx + 1, CB + 4, '#ffd8a0');
      rect(ctx, tx + 1, CB + 5, 1, sy - CB - 5, '#2a323c'); rect(ctx, tx + 3, CB + 5, 1, sy - CB - 5, '#2a323c');
      rect(ctx, tx, sy, 5, 1, '#c8a040');
      if (cr.k === 3 && wave(t, cr.kh, cr.ph2) < 0) rect(ctx, tx, sy + 1, 5, 2, '#7a4428');   // a box on the hook
    } else if (cr.type === 'move') {
      const th = moveTh(t), [tx2, ty2] = upTip(c, th);
      pline(c, CB + 1, tx2, ty2, CR.hi); pline(c, CB + 2, tx2, ty2 + 1, CR.mid);
      pline(c + 12, APEX, tx2 + 1, ty2 - 1, CR.dk);
    }
  }
  for (const m of MASTS) halo(HL.mast, m, 120, 0.9);
  ctx.globalAlpha = 0.17; ctx.drawImage(HAZE_C, 0, 0); ctx.globalAlpha = 1;
  for (const cr of CRANES) {
    const c = cr.c, on = frac(t / 2 + cr.lit * 0.25) < 0.5;
    craneRed(c + 12, APEX - 2, on);
    let tip;
    if (cr.type === 'flat') tip = [c - BOOM_LEN, CB - 1];
    else if (cr.type === 'up') { const [x, y] = upTip(c, UP_TH); tip = [x, y - 1]; }
    else { const [x, y] = upTip(c, moveTh(t)); tip = [x, y - 1]; }
    craneRed(tip[0], tip[1], !on);
  }
}

/* ================= Per-frame: the Elbe, the container ship ================= */
function drawElbe(t, G) {
  ctx.drawImage(WATER_C, 0, HOR);
  reflect(ctx, t, { top: HOR, rows: QUAY - HOR, x: 0, w: EP.x0 - 2, squash: 1.7, alpha: 0.44, amp: 0.9, grow: 0.06 });
  reflect(ctx, t, { top: HOR, rows: EP.wl - HOR, x: EP.x1 + 3, w: FB.x0 - EP.x1 - 3, squash: 1.7, alpha: 0.44 });
  const s = step(t, 960);
  ctx.fillStyle = '#c8d6e4';
  for (let k = 0; k < 26; k++) {                                     // whitecaps whipped up by the gusts
    if (hash(k, s, 31) > 0.25 + 0.5 * G) continue;
    const x = Math.floor(hash(k, s, 32) * (EP.x0 - 4)), y = HOR + 3 + Math.floor(hash(k, s, 33) * 34);
    ctx.globalAlpha = 0.35 + 0.3 * hash(k, s, 34); ctx.fillRect(x, y, 2 + (k & 1), 1);
  }
  ctx.globalAlpha = 1;
}
function drawShip(t) {
  if (t < SHIP.t0 || t >= SHIP.t1) return;
  const u = (t - SHIP.t0) / (SHIP.t1 - SHIP.t0), bow = Math.round(lerp(SHIP.x0, SHIP.x1, u));
  const x = bow - (SHIP_W - 1), y = SHIP_WL - SHIP_H + 1;
  for (let j = 0; j < 14; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.12));
    ctx.globalAlpha = 0.32 * (1 - j / 14);
    ctx.drawImage(SHIP_C, 0, SHIP_H - 1 - j, SHIP_W, 1, x + off, SHIP_WL + 1 + j, SHIP_W, 1);
  }
  ctx.globalAlpha = 1;
  const s = step(t, 1440);
  ctx.fillStyle = '#dfe8f2';
  for (let k = 0; k < 40; k++) {                                     // wake trailing from the stern
    const wx = x - k * 2 - ((k * k) >> 4), wy = SHIP_WL + 1 + (k % 3 === 0 ? 1 : 0) + (k >> 4);
    if (hash(k, s, 17) < 0.7 - k * 0.015) { ctx.globalAlpha = 0.7 - k * 0.015; ctx.fillRect(wx, wy, 2, 1); }
  }
  for (let k = 0; k < 8; k++) if (hash(k, s, 18) < 0.8) { ctx.globalAlpha = 0.85; ctx.fillRect(bow - 4 + k, SHIP_WL - (k > 4 ? 1 : 0), 1, 1); }  // bow wave
  ctx.globalAlpha = 1;
  ctx.drawImage(SHIP_C, x, y);
  streak(ctx, t, x + 101, 6, '#ffd48a', 0.28, 5, SHIP_WL + 2, SHIP_WL + 24);
  halo(HL.ship, x + 101, y + 12, 0.8);
  halo(HL.white, x + 101, y, 1); px(ctx, x + 101, y, '#ffffff');
  halo(HL.white, x + 141, y + 17, 0.9); px(ctx, x + 141, y + 17, '#ffffff');
  halo(HL.red, x + 92, y + 5, 0.9); px(ctx, x + 92, y + 5, '#ff4040');
  px(ctx, x + 1, y + 24, '#fff4d8');
  for (const dx of [40, 70, 124]) halo(HL.boat, x + dx, y + 12, 0.5);
  const hs = t - HORN_T;                                             // steam from the whistle on the horn blast
  if (hs >= 0 && hs < 4.5) for (let k = 0; k < 7; k++) {
    const v = frac(hs / 1.4 + k / 7), sx = x + 103 + v * 8 + v * v * 14 * (1 + gust(t)), sy = y - 1 - v * 11;
    const rr = 1 + Math.floor(v * 4);
    ctx.globalAlpha = 0.75 * (1 - v) * Math.min(1, v * 6) * (1 - smooth(3.2, 4.5, hs));
    ctx.fillStyle = '#e8eef4'; ctx.fillRect(Math.round(sx) - rr, Math.round(sy) - rr, rr * 2, rr * 2 - 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: the Elbphilharmonie ================= */
function drawElbphi(t) {
  halo(HL.epGlass, EP_CX, 100, 0.9 + 0.1 * wave(t, 6));
  ctx.drawImage(EPH.c, 0, 0);
  for (const w of EPH.warm) if (winOn(w, 40, t)) for (const x of w.cells) {
    rect(ctx, x, w.y, 3, 1, WARM_A); rect(ctx, x, w.y + 1, 3, 1, WARM_B);
  }
  const st = step(t, 960);
  ctx.fillStyle = '#f6fbff';
  for (const s of EPH.twinkle) if (hash(s.id, st, 21) < 0.06) ctx.fillRect(s.x, s.y, 1, 1);
  const q = clamp((frac(2 * t / LOOP + 0.3) - 0.15) / 0.4, 0, 1);    // the sheen sweep, twice per loop
  if (q > 0 && q < 1) {
    const [sc, sx] = SHEEN_S, bw = SHEEN_C.width;
    sx.globalCompositeOperation = 'copy';
    sx.drawImage(SHEEN_C, Math.round(lerp(-bw, sc.width, smooth(0, 1, q))), 0);
    sx.globalCompositeOperation = 'destination-in';
    sx.drawImage(EPH.mask, 0, 0);
    sx.globalCompositeOperation = 'source-over';
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.3;
    ctx.drawImage(sc, EP.x0, 0);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  for (let k = 0; k < 4; k++) halo(HL.epPlaza, EP.x0 + 16 + k * 30, PZ.y + 2, 0.85 + 0.15 * wave(t, 4 + k, k * 0.21));
  drawPlazaFolk(t);
  for (const lx of EP_LAMPS) halo(HL.epLamp, lx, EP.base - 1, 0.9);
  EP_RED.forEach(([x, y], i) => { const on = frac(t / 3 + i * 0.5) < 0.45; px(ctx, x, y, on ? '#ff4a3a' : '#6a2020'); if (on) halo(HL.red, x, y, 0.9); });
}

/* ================= Per-frame: Speicherstadt, the Michel, the footbridge ================= */
function flick(t, list, id) {
  let v = 1;
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); v = hv > 0.45 ? 1 : hv > 0.2 ? 0.4 : 0.1; }
  return v;
}
function bellLevel(t) {
  let b = 0;
  for (const tb of BELLS) { const dt = t - tb; if (dt >= 0 && dt < 3) b = Math.max(b, Math.exp(-dt * 1.3) * (1 - dt / 3)); }
  return b;
}
function drawFlag(t, G) {
  const amp = 0.5 + 1.3 * G, X = 447, Y = 21;
  for (let i = 0; i < 16; i++) {
    const ic = i >= 2 && i <= 12 ? 7 : i;                               // the castle stays in one piece
    const ph = TAU * 480 * t / LOOP - ic * 0.55, dy = Math.round(Math.sin(ph) * amp * (ic / 15)), shade = Math.cos(ph) < -0.5;
    if (i > 13 && hash(i, step(t, 2400), 7) < 0.3 * G) continue;       // the fly end snapping
    for (let j = 0; j < 10; j++) {
      const c = FLAG_ROWS[j][i];
      px(ctx, X + i, Y + j + dy, c === 'W' ? (shade ? '#d4d4dc' : '#f4f4f8') : c === 'D' ? (shade ? '#2e0c10' : '#4a1418') : (shade ? '#9a1c24' : '#d42a30'));
    }
  }
}
/* the billboard: glow on the brick, structure, the face (own ad or the pitch), then the lamps' halos */
const BB_FLICK = [[88.4, 90.1], [196.2, 197]];                      // the right-hand floodlamp sputters
function drawPoster(t, lv) {
  ctx.drawImage(POSTERS[0], BB.x, BB.y);
  if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(POSTERS[1], BB.x, BB.y); ctx.globalAlpha = 1; }
  const flip = hash(step(t, 48), 0, 61) < 0.55 && frac(t * 48 / LOOP) < 0.14 && (step(t, 2400) & 1);   // the herring's tail
  const X = BB.x + ROLL.x, Y = BB.y + ROLL.y;
  ctx.fillStyle = TAIL;
  ctx.fillRect(X + 1, Y + 4, 1, 3); ctx.fillRect(X, Y + (flip ? 2 : 3), 1, 1); ctx.fillRect(X, Y + (flip ? 6 : 7), 1, 1);
}
function drawBillboard(t, pitch) {
  const lv = flick(t, BB_FLICK, 17);
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + BB.h, (0.88 + 0.12 * wave(t, 8, 0.6)) * (2 + lv) / 3);
  ctx.drawImage(BB_C, BBC.x, BBC.y);
  if (!PITCH.covers(t, pitch)) drawPoster(t, lv);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const eye = hash(9, step(t, 200), 2) < 0.6;                        // a gull on the cornice, eyeing the Fischbroetchen
  ctx.drawImage(eye ? GULL_PERCH_F : PERCH_GULL, BB.x + 8, BB.y - 7);
  BB_LAMPS.forEach((lx, i) => {
    const a = (i === 2 ? lv : 1) * (0.92 + 0.08 * wave(t, 640 + i * 41));
    if (a < 0.9) rect(ctx, lx - 2, BB_LAMP_Y, 5, 1, `rgba(40,26,18,${(0.85 * (1 - a)).toFixed(3)})`);
    halo(HL.bbLamp, lx, BB_LAMP_Y, a);
  });
}
function drawSpeicherstadt(t, G, pitch, billboard, clock) {
  ctx.drawImage(GAP.c, 0, 0);
  hands(ctx, MI.cx, MI.clock, clock, 240, 1, 2, '#4a4438', '#4a4438', null);   // the Michel's clock: Hamburg's time
  for (const w of GAP.wins) if (winOn(w, 90, t)) px(ctx, w.x, w.y, '#e0a058');
  halo(HL.michel, MI.cx, MI.tip + 30, 0.7); halo(HL.clock, MI.cx, MI.clock, 0.6);
  const b = bellLevel(t);
  if (b > 0.02) {
    halo(HL.bell, MI.cx, MI.tip + 30, b);
    for (const tb of BELLS) {                                         // faint sound rings from the belfry
      const dt = t - tb;
      if (dt < 0 || dt > 2.4) continue;
      const rr = 6 + dt * 30, n = Math.floor(rr * 2);
      ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#ffd890'; ctx.globalAlpha = (1 - dt / 2.4) * 0.4;
      for (let j = 0; j < n; j++) { const a = j / n * TAU; ctx.fillRect(Math.round(MI.cx + Math.cos(a) * rr), Math.round(MI.tip + 30 + Math.sin(a) * rr * 0.8), 1, 1); }
      ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
    }
  }
  ctx.drawImage(FARB.c, 0, 0);
  for (const w of FARB.wins) if (winOn(w, 110, t)) { rect(ctx, w.x, w.y, 2, 5, WARM_B); rect(ctx, w.x, w.y, 2, 1, WARM_A); }
  for (const s of FB_SPOTS) halo(HL.floodS, s, FB.base - 12, 0.9);
  if (billboard) drawBillboard(t, pitch);
  ctx.drawImage(NEAR.c, 0, 0);
  for (const w of NEAR.wins) if (winOn(w, 130, t)) {
    rect(ctx, w.x, w.y + 1, 3, 9, WARM_B); rect(ctx, w.x, w.y + 2, 3, 2, WARM_A); px(ctx, w.x + 1, w.y, WARM_A);
    halo(HL.win, w.x + 1, w.y + 5, 0.7);
  }
  for (const s of NB_SPOTS) halo(HL.flood, s, NB.base - 16, 0.95);
  halo(HL.floodS, 446, 88, 0.9);
  halo(HL.win, 446, 60, 0.9);
  drawFlag(t, G);
  const sw = (1.2 + 2.2 * G) * wave(t, 40, 0.1) + 2 * G;              // the hoist rope, blown about
  pline(446, 69, 446 + sw, 100, '#3a2c22');
  rect(ctx, Math.round(446 + sw) - 1, 100, 3, 1, '#8a8a90'); px(ctx, Math.round(446 + sw) + 1, 101, '#8a8a90');
  drawBridgeFolk(t);                                                   // behind the railing's lattice
  ctx.drawImage(BRIDGE_C, 0, 0);
  const bf = flick(t, BRG_FLICK, 5);
  BRG_LAMPS.forEach((lx, i) => {
    const a = (i === 1 ? bf : 1) * (0.88 + 0.12 * wave(t, 700 + i * 37));
    if (a < 0.5) rect(ctx, lx, BRG.deck - 24, 1, 4, '#5a4a30');
    halo(HL.blamp, lx, BRG.deck - 22, a); halo(HL.blampCore, lx, BRG.deck - 22, a);
  });
  const look = hash(8, step(t, 300), 2) < 0.5;                        // a gull on the handrail, turning its head
  ctx.drawImage(look ? GULL_PERCH_F : PERCH_GULL, 364, BRG.deck - 12);
}

/* ================= Per-frame: the basin in front of us ================= */
function drawRings(list, t, x0, x1, y0, y1, maxR, waterOnly) {
  for (const g of list) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.45) continue;
    const s = u / 0.45, x = Math.floor(x0 + hash(g.id, cyc, 1) * (x1 - x0)), y = Math.floor(y0 + hash(g.id, cyc, 2) * (y1 - y0));
    if (waterOnly && y < waterlineAt(x) + 3) continue;
    const rr = 1 + s * maxR, ry = rr * 0.36, pts = 6 + Math.floor(rr * 2.5);
    ctx.fillStyle = STREAKS.some(st => Math.abs(st.x - x) < st.w + 2 && y > st.y0) ? '#ffd8a0' : '#9ab0c8';
    ctx.globalAlpha = (1 - s) * 0.5;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
function drawDalben(t) {
  DALBEN.forEach((d, i) => {
    const D = DALB_C[i], w = D.c.width, h = D.c.height, x = d.x, top = d.wl - h + 1;
    for (let j = 0; j < 10; j++) {
      const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.4 + j * 0.12));
      ctx.globalAlpha = 0.34 * (1 - j / 10);
      ctx.drawImage(D.c, 0, h - 1 - j, w, 1, x + off, d.wl + 1 + j, w, 1);
    }
    ctx.globalAlpha = 1;
    ctx.drawImage(D.c, x, top);
    const s = step(t, 960);
    ctx.fillStyle = '#c8d4e0';
    for (let k = 0; k < 5; k++) if (hash(k + i * 9, s, 51) < 0.5) { ctx.globalAlpha = 0.6; ctx.fillRect(x - 1 + Math.floor(hash(k, s, 52) * (w + 2)), d.wl, 1, 1); }
    ctx.globalAlpha = 1;
    if (i === 0) {                                                    // green light on the tallest pile
      const on = frac(t / 4) < 0.6;
      px(ctx, x + D.top, top + 1, on ? '#60ff90' : '#1a4a2a'); if (on) halo(HL.green, x + D.top, top + 1, 0.9);
      if (on) streak(ctx, t, x + D.top, 2, '#60ff90', 0.2, 40, d.wl + 1, QUAY);
    } else ctx.drawImage(hash(4, step(t, 240), 2) < 0.5 ? GULL_PERCH_F : PERCH_GULL, x + D.top - 2, top - 4);
  });
}
function drawSeal(t) {
  if (t < SEAL.t0 || t >= SEAL.t1) return;
  const s = t - SEAL.t0, dur = SEAL.t1 - SEAL.t0;
  const rise = smooth(0.2, 1.4, s) * (1 - smooth(dur - 1.8, dur - 0.6, s));
  const vis = Math.round(rise * 5), bob = Math.round(0.6 * wave(t, 80));
  const flip = hash(3, step(t, 160), 5) < 0.5;
  const y = SEAL.wl - vis + bob, x = SEAL.x;
  if (vis > 0) ctx.drawImage(flip ? SEAL_F : SEAL_C, 0, 0, 7, vis, x, y, 7, vis);
  const rr = 3 + frac(s / 2.2) * 6;                                   // ripples around the head
  ctx.fillStyle = '#9ab0c8'; ctx.globalAlpha = (1 - frac(s / 2.2)) * 0.5 * Math.min(1, rise * 2 + (s > dur - 2 ? 1 : 0));
  for (let j = 0; j < 16; j++) { const a = j / 16 * TAU; ctx.fillRect(Math.round(x + 3 + Math.cos(a) * rr), Math.round(SEAL.wl + 1 + Math.sin(a) * rr * 0.35), 1, 1); }
  ctx.globalAlpha = 1;
  const ds = s - (dur - 1.2);                                         // the dive: a splash
  if (ds > 0 && ds < 1.2) {
    ctx.fillStyle = '#dfe8f2';
    for (let k = 0; k < 6; k++) {
      const a = -Math.PI / 2 + (k - 2.5) * 0.4, v = ds * 12;
      ctx.globalAlpha = 1 - ds / 1.2;
      ctx.fillRect(Math.round(x + 3 + Math.cos(a) * v), Math.round(SEAL.wl - 2 + Math.sin(a) * v * 0.6 + ds * ds * 9), 1, 1);
    }
    ctx.globalAlpha = 1;
  }
}
function drawBasin(t, G, rain) {
  reflect(ctx, t, { top: EP.wl, rows: QUAY - EP.wl, x: EP.x0 - 2, w: EP.x1 - EP.x0 + 5, squash: 2.1, alpha: 0.52, amp: 0.8, grow: 0.07 });
  reflect(ctx, t, { top: FB.base, rows: QUAY - FB.base, x: FB.x0, w: NB.x0 - FB.x0, squash: 1.5, alpha: 0.5, amp: 0.8, grow: 0.07 });
  reflect(ctx, t, { top: NB.base, rows: QUAY - NB.base, x: NB.x0, w: W - NB.x0, squash: 1.5, alpha: 0.52, amp: 0.8, grow: 0.07 });
  ctx.fillStyle = 'rgba(8,16,26,0.2)';
  for (const [a, y0, b] of WATER_RECTS) ctx.fillRect(a, y0, b - a, QUAY - y0);
  for (let y = HOR + 2, n = 0; y < QUAY; y += 2, n++) for (let j = 0; j < 4; j++) {   // wind chop running downwind
    const k = 4 + ((n * 7 + j * 5) % 9), len = 4 + Math.floor(hash(n, j, 41) * 14);
    const x = Math.round(frac(hash(n, j, 40) + k * t / LOOP) * (W + 40)) - 20;
    if (y < waterlineAt(x) + 1 || y < waterlineAt(x + len) + 1) continue;
    ctx.globalAlpha = j === 3 ? 0.24 + 0.2 * G : 0.4;
    rect(ctx, x, y + (j & 1), len, 1, j === 3 ? '#8a9cb4' : '#08101a');
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0 + 1, QUAY);
  if (rain) drawRings(RINGS, t, 0, W, HOR + 4, QUAY - 2, 5, true);
  drawDalben(t);
  drawSeal(t);
}

/* ================= Per-frame: ferry and Barkasse ================= */
function drawBoatPass(spr, P, t, dir, lights) {
  if (t < P.t0 || t >= P.t1) return;
  const w = spr.width, h = spr.height, x = Math.round(lerp(P.x0, P.x1, (t - P.t0) / (P.t1 - P.t0))), y = P.wl - h + 1;
  const rows = Math.min(10, QUAY - P.wl - 1);
  for (let j = 0; j < rows; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.14));
    ctx.globalAlpha = 0.34 * (1 - j / rows);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, P.wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
  const s = step(t, 1200), stern = dir > 0 ? x + 1 : x + w - 2, bow = dir > 0 ? x + w - 1 : x;
  ctx.fillStyle = '#e6eef6';
  for (let d = 1; d < 44; d++) {                                     // wake and bow wave
    const xx = stern - dir * d, fade = 1 - d / 44;
    if (hash(d, s, 41) < 0.7) { ctx.globalAlpha = 0.7 * fade; ctx.fillRect(xx, P.wl + 1 + Math.round(d * 0.06), 1, 1); }
    if (d < 26 && hash(d, s, 43) < 0.8 * fade) { ctx.globalAlpha = 0.85 * fade; ctx.fillRect(xx, P.wl + (hash(d, s, 44) < 0.5 ? 0 : 1), 1, 1); }
  }
  ctx.globalAlpha = 0.9;
  for (let k = 0; k < 5; k++) if (hash(k, s, 45) < 0.8) ctx.fillRect(bow + dir * (k >> 1), P.wl - (k & 1), 1, 1);
  ctx.globalAlpha = 1;
  ctx.drawImage(spr, x, y);
  lights(x, y, w, h);
}
function ferryLights(x, y, w) {
  for (const o of [10, 22, 34]) streak(ctx, t_, x + o, 2, '#ffd98a', 0.3, o, FERRY.wl + 1, QUAY);
  halo(HL.ferry, x + (w >> 1), y + 8, 0.7);
  halo(HL.white, x + 31, y, 1); px(ctx, x + 31, y, '#ffffff');
  const fl = Math.round(wave(t_, 300) * 1.2);                          // stern pennant
  px(ctx, x + 45, y + 3, '#5a6068'); px(ctx, x + 45, y + 4, '#5a6068');
  rect(ctx, x + 46, y + 3 + (fl > 0 ? 1 : 0), 3, 1, '#d42a30'); px(ctx, x + 49, y + 3 + (fl < 0 ? 1 : 0), '#d42a30');
}
function barkLights(x, y, w) {
  for (const o of [10, 20]) streak(ctx, t_, x + o, 2, '#ffcf80', 0.32, o + 3, BARK.wl + 1, QUAY);
  halo(HL.boat, x + 12, y + 3, 0.9); halo(HL.boat, x + 24, y + 3, 0.9);
  px(ctx, x + 1, y + 6, '#fff4d8'); halo(HL.white, x + 1, y + 6, 0.6);
  const G = gust(t_), n = 4 + Math.round(G * 2);                    // bow pennant streaming downwind
  for (let k = 0; k < n; k++) px(ctx, x + 36 + k, y + Math.round(k * 0.3 + (wave(t_, 420, k * 0.12) > 0 ? 0 : 1) * (k / n)), '#d42a30');
}
let t_ = 0;                                                          // current frame time for the light callbacks

/* ================= Per-frame: gulls in the wind ================= */
function drawMidGulls(t, G) {
  for (const g of MID_GULLS) {                                       // hanging on the wind, drifting a little
    const a = TAU * (g.k * t / LOOP + g.ph), x = g.cx + g.rx * Math.sin(a) + 3 * G, y = g.cy + g.ry * Math.sin(2 * a) - 2 * G;
    ctx.drawImage(GULL_SPR[1][gullFrame(t, g)][1], Math.round(x) - 4, Math.round(y) - 2);
  }
}
function drawNearGulls(t, G) {
  for (const g of NEAR_GULLS) {                                      // blown past on a gust
    if (t < g.a || t >= g.b) continue;
    const s = (t - g.a) / (g.b - g.a), x = lerp(-20, W + 6, s * s * 0.3 + s * 0.7);
    const y = g.y0 + g.dip * Math.sin(Math.PI * s) + 3 * Math.sin(TAU * 3 * s) * G;
    const fr = (s < 0.25 || (s > 0.55 && s < 0.75)) ? [0, 1, 2, 1][step(t, 1680) % 4] : 1;
    ctx.drawImage(GULL_SPR[2][fr][0], Math.round(x), Math.round(y));
  }
}

/* ================= Per-frame: the quay, the cat, the lantern, a walker ================= */
function drawCat(t, G) {
  const X = CAT.x - 4, Y = 218, CG = '#6e707c';
  ctx.drawImage(CAT_C, X, Y);
  if (hash(6, step(t, 480), 2) < 0.06) px(ctx, X + 1, Y + 3, CG);                    // blink
  if (EAR_FLICKS.some(e => t >= e && t < e + 0.3)) { px(ctx, X + 5, Y, '#1a1c22'); px(ctx, X + 6, Y + 1, '#b4bece'); }   // ear twitch
  const tip = Math.round(1.3 * wave(t, 23, 0.2) + 1.2 * G);             // tail down the bollard's side
  ctx.fillStyle = '#5a5c68';
  ctx.fillRect(X + 9, Y + 10, 1, 3); ctx.fillRect(X + 10, Y + 12, 1, 4);
  for (let k = 0; k < 4; k++) ctx.fillRect(X + 10 + Math.round(tip * k / 3), Y + 16 + k, 1, 1);
  px(ctx, X + 10 + tip, Y + 19, '#2e3038');
}
function drawLantern(t, G) {
  const lf = flick(t, LAMP_FLICK, 11);
  const a = (0.05 + 0.1 * G) * Math.sin(TAU * 170 * t / LOOP) + 0.04 * wave(t, 37, 0.3) + 0.07 * G;
  const X = LAMP.arm - 3, Y = LAMP.top + 9;
  for (let j = 0; j < LANTERN_C.height; j++) ctx.drawImage(LANTERN_C, 0, j, 7, 1, X + Math.round(a * (j + 3)), Y + j, 7, 1);
  const cx = LAMP.arm + Math.round(a * 8), cy = Y + 5, li = (0.94 + 0.06 * wave(t, 1500, 0.3)) * lf;
  if (li < 0.9) rect(ctx, cx - 2, cy - 2, 3, 5, `rgba(30,18,8,${(0.9 - li).toFixed(3)})`);
  halo(HL.lamp, cx, cy, li); halo(HL.lampCore, cx, cy, li);
}
function drawForeground(t, G, rain) {
  ctx.drawImage(QUAY_C, 0, 0);
  if (rain) drawRings(PUDDLE_RINGS, t, 60, 460, QUAY + 9, H - 3, 3, false);
  streak(ctx, t, LAMP.arm, 5, '#ffc878', 0.36, 21, QUAY + 8, H);
  streak(ctx, t, EP_CX, 12, '#88b0d8', 0.16, 22, QUAY + 8, H);
  drawCat(t, G);
  drawQuayFolk(t, G);
  drawLantern(t, G);
}

/* ================= People =================
   A few, slow, each doing something. On our quay a couple stands at the edge all loop looking at the
   Elbphilharmonie (she leans her head on his shoulder, now and then he points up at the roof), someone pushes a bike
   along with the wind (192-230 s) and the walker in the oilskin heads into it (124-172 s); each has a dim mirror image
   on the wet stone. A jogger crosses the footbridge there and back (22 s, 196 s) and a dog walker crosses it (64-84 s),
   the dog stopping to look up at the gull on the handrail. Small dark figures stroll the Plaza against its glow. */
const NIGHT = '#0a1018', RIM = '#a8c4e0';            // shade sinks into the night; edges catch the glass's cool light
let MIR = -1;                                        // the feet row a figure is mirrored about on the wet stone (-1: upright)
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, MIR < 0 ? y : 2 * MIR + 2 - y - h, w, h); };
function seg(x0, y0, x1, y1, col) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) R(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), 1, 1, col);
}
function mixHex(a, b, u) {
  const A = rgbOf(a), B = rgbOf(b);
  return '#' + A.map((v, k) => Math.round(lerp(v, B[k], u)).toString(16).padStart(2, '0')).join('');
}
/** a look: the proportions for a height of h px, and its colours dimmed for the night */
function look(h, coat, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk), hair = o.hair ?? '#3a2618', th = Math.round(h * 0.38);
  return { hh: 5, hw: 4, th, tw: h >= 24 ? 7 : 6, ll: h - 5 - th, hem: o.hem ?? 0,
    coat: m(coat), coatD: mixHex(coat, NIGHT, dk + 0.3), coatL: mixHex(coat, RIM, 0.4), bot: m(bot), shoe: m('#1c1a20'),
    skin: m(o.skin ?? '#d8a888'), hair: m(hair), hairL: mixHex(hair, RIM, 0.3), hood: !!o.hood,
    cap: o.cap ? m(o.cap) : null, beanie: o.beanie ? m(o.beanie) : null, scarf: o.scarf ? m(o.scarf) : null };
}
/**
 * One person on our quay, feet on row fy, centred on column x. o.view: 0 from behind (looking out over the water),
 * 2 in profile (o.dir 1 right, -1 left; o.step the stride frame). o.aL / o.aR: 0 hands in the pockets, 1 pointing up,
 * 3 out (round a partner's shoulders, onto a handlebar; o.reach px). o.lean tips the head towards a neighbour;
 * o.flap blows the scarf's end and the coat's hem downwind (to the right).
 */
function person(x, fy, P, o = {}) {
  const side = o.view === 2, dir = o.dir || 1, { hw, hh, th, ll } = P, bw = side ? P.tw - 2 : P.tw, tx = x - (bw >> 1);
  const ty = fy - ll - th + 1, hy = ty - hh, ly = ty + th, fl = o.flap || 0;
  if (side && o.step) {
    R(dir > 0 ? x + 1 : x - 2, ly, 2, ll, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);          // leading leg
    R(dir > 0 ? x - 2 : x + 1, ly, 2, ll - 1, P.bot); R(dir > 0 ? x - 3 : x + 1, fy - 1, 3, 1, P.shoe);  // trailing leg, heel up
  } else if (side) { R(x - 1, ly, 2, ll, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
  else { R(tx + 1, ly, 2, ll, P.bot); R(tx + bw - 3, ly, 2, ll, P.bot); R(tx + 1, fy, 2, 1, P.shoe); R(tx + bw - 3, fy, 2, 1, P.shoe); }
  R(tx, ty, bw, th + P.hem, P.coat);
  if (!side) R(tx + bw - 1, ty + 1, 1, th + P.hem - 1, P.coatD);
  if (fl > 1 && P.hem) R(tx + bw, ly + P.hem - 2, 1, 2, P.coat);
  R(tx, ty, bw, 1, P.coatL);                                            // the glass's light on the shoulders
  if (side) {
    if (o.aR === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.coatD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else R(x - (o.step ? dir : 0), ty + 1, 1, th - 2, P.coatD);
  } else {
    const arm = (ax, st, sg) => {
      if (st === 1) { seg(ax, ty + 1, ax + sg * 2, hy - 2, P.coatL); R(ax + sg * 2, hy - 4, 1, 2, P.skin); }   // lit by what it points at
      else if (st === 3) R(sg < 0 ? ax - (o.reach || 3) + 1 : ax, ty + 2, o.reach || 3, 1, P.coatD);
      else R(ax, ty + 1, 1, th - 2, P.coatD);
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  if (P.scarf) {
    R(tx + 1, ty, bw - 2, 1, P.scarf);
    if (!side) { R(tx + bw - 2, ty + 1, 1, 2, P.scarf); if (fl) R(tx + bw - 1, ty + 1, fl, 1, P.scarf); }
  }
  const hx = x - (hw >> 1) + (o.lean || 0), y = hy + (o.lean ? 1 : 0);
  if (side) { R(hx, y, hw, hh, P.skin); R(hx, y, hw, 2, P.hair); R(dir > 0 ? hx : hx + hw - 1, y, 1, hh - 1, P.hair); }
  else { R(hx, y, hw, hh, P.hair); R(hx, y, hw, 1, P.hairL); R(hx + 1, y + hh - 1, hw - 2, 1, P.skin); }
  if (P.hood) {                                                         // the raincoat's hood, up
    R(hx - 1, y - 1, hw + 2, hh, P.coat); R(hx - 1, y - 1, hw + 2, 1, P.coatL); R(hx, y - 2, hw, 1, P.coatL);
    if (side) R(dir > 0 ? hx + hw - 1 : hx, y + 1, 1, 3, P.skin);
  }
  if (P.cap) {                                                          // an Elbsegler, the Hamburg skipper's cap
    R(hx - 1, y, hw + 2, 1, P.cap); R(hx, y - 1, hw, 1, P.cap); R(hx, y - 1, hw, 1, mixHex(P.cap, RIM, 0.3));
    if (side) R(dir > 0 ? hx + hw : hx - 2, y + 1, 2, 1, '#0c0c12');
  }
  if (P.beanie) { R(hx, y - 1, hw, 2, P.beanie); R(hx + 1, y - 2, 2, 1, P.beanie); }
}
const HIM = look(25, '#26324a', '#1c2030', { cap: '#1c2438', scarf: '#d8d0c0', hair: '#5a4a3a' });
const HER = look(23, '#b0343a', '#22202c', { hood: true, hem: 3, dk: 0.26 });
const PUSHER = look(24, '#2e6e72', '#2a3044', { beanie: '#c8963e', hair: '#2a1a12', hem: 1 });
const COUPLE = { x: 205, fy: 249 };                  // him; she stands 7 px to his right
const CPL = { x: COUPLE.x - 7, y: COUPLE.fy - 29, w: 24, h: 30 };   // the couple's baked poses: this box of the frame
const BIKE_BOX = { dx: -18, h: 27, w: 31 };          // the bike and its pusher, baked: x - 18 .. x + 12, fy - 26 .. fy
/** paint with person() and friends into a w x h canvas of its own (build time) */
function bake(w, h, draw) {
  const [c, x] = makeCanvas(w, h), keep = ctx;
  ctx = x; MIR = -1; draw(); ctx = keep;
  return c;
}
/* the bike: a town bike with a rack, pushed by the handlebar; its lamp lit, a spoke reflector turning (per frame) */
const WHEEL = [];
for (let dy = -5; dy <= 5; dy++) for (let dx = -5; dx <= 5; dx++) { const d = Math.hypot(dx, dy); if (d > 4.5 && d < 5.5) WHEEL.push([dx, dy]); }
function bikeAt(x, fy, stride) {
  const gx = x + 4, gy = fy - 16, F = [gx + 2, fy - 5], B = [gx - 16, fy - 5], BBR = [gx - 8, fy - 4], S = [gx - 11, gy + 2];
  const fr = '#4e7a6a', frL = '#8ab4a0';
  for (const [cx, cy] of [F, B]) {                                     // tyres with reflective sidewalls
    for (const [dx, dy] of WHEEL) R(cx + dx, cy + dy, 1, 1, dy < -1 ? '#9aa4b0' : dy < 3 ? '#646c78' : '#30343c');
    R(cx, cy, 1, 1, '#6a727e');
  }
  seg(B[0], B[1], BBR[0], BBR[1], fr); seg(B[0], B[1], S[0] + 1, S[1] + 1, fr);           // chain and seat stays
  seg(S[0] + 1, S[1], BBR[0], BBR[1], fr); seg(S[0] + 1, S[1] + 1, gx - 1, gy + 2, frL);  // seat tube, top tube
  seg(gx - 1, gy + 3, BBR[0], BBR[1], fr); seg(gx - 1, gy + 2, F[0], F[1], fr);           // down tube, fork
  R(S[0] - 1, S[1] - 1, 4, 1, '#0c0c10'); R(gx - 1, gy, 1, 2, fr); R(gx - 2, gy, 3, 1, '#0c0c10');   // saddle, stem, bars
  R(B[0] - 3, gy + 4, 8, 1, fr); R(B[0] - 4, gy + 5, 1, 1, '#2a0c0c');                     // rack, rear light housing
  R(BBR[0] - 1, BBR[1] + 1, 3, 1, '#2a2e36');                                              // pedals
  R(gx + 2, gy + 4, 2, 1, '#fff2d4'); R(B[0] - 4, gy + 4, 1, 1, '#ff3a30');                // lamp, rear light
  person(x, fy, PUSHER, { view: 2, dir: 1, step: stride, aR: 3 });
}
let COUPLE_C, BIKE_C;
function bakeFolk() {
  COUPLE_C = [];                                                       // [lean][point][flap]
  for (const lean of [0, 1]) for (const point of [0, 1]) for (let flap = 0; flap < 4; flap++)
    COUPLE_C[lean * 8 + point * 4 + flap] = bake(CPL.w, CPL.h, () => {
      person(COUPLE.x + 7 - CPL.x, COUPLE.fy - CPL.y, HER, { lean: lean ? -2 : 0, flap });
      person(COUPLE.x - CPL.x, COUPLE.fy - CPL.y, HIM, { aR: 3, reach: 4, aL: point, flap });
    });
  BIKE_C = [0, 1].map(stride => bake(BIKE_BOX.w, BIKE_BOX.h, () => bikeAt(-BIKE_BOX.dx, BIKE_BOX.h - 1, stride)));
}
/** a baked figure whose feet are on row fy, upright or (mir) mirrored about that row on the wet stone */
function putFolk(c, x, y, fy, mir) {
  if (!mir) { ctx.drawImage(c, x, y); return; }
  ctx.setTransform(1, 0, 0, -1, 0, 2 * fy + 2); ctx.drawImage(c, x, y); ctx.setTransform(1, 0, 0, 1, 0, 0);
}
function drawCouple(t, G, mir) {
  const point = frac(t / 60 + 0.62) < 0.07, lean = !point && frac(t / 40 + 0.15) < 0.45;
  const flap = clamp(Math.round(G * 2.6 + 0.6 * G * wave(t, 300, 0.2)), 0, 3);
  putFolk(COUPLE_C[(lean ? 8 : 0) + (point ? 4 : 0) + flap], CPL.x, CPL.y, COUPLE.fy, mir);
}
function drawBike(t, G, mir) {
  if (t < BIKE.t0 || t >= BIKE.t1) return;
  const fy = BIKE.fy, x = Math.round(lerp(BIKE.x0, BIKE.x1, (t - BIKE.t0) / (BIKE.t1 - BIKE.t0))), roll = (x - BIKE.x0) / 5;
  putFolk(BIKE_C[Math.floor(t * 3.2) & 1], x + BIKE_BOX.dx, fy - BIKE_BOX.h + 1, fy, mir);
  MIR = mir ? fy : -1;
  for (const cx of [x + 6, x - 12]) R(cx + Math.round(3 * Math.cos(roll)), fy - 5 + Math.round(3 * Math.sin(roll)), 1, 1, '#e8a030');   // spoke reflectors
  MIR = -1;
  if (!mir) {
    halo(HL.bike, x + 7, fy - 12, 0.9); halo(HL.red, x - 16, fy - 12, 0.7);
    streak(ctx, t, x + 7, 2, '#ffecc8', 0.3, 31, fy + 1, H);                               // its light on the wet stone
  }
}
function drawWalker(t, G, mir) {
  if (t < WALKER.t0 || t >= WALKER.t1) return;
  MIR = mir ? WALKER.fy : -1;
  const u = (t - WALKER.t0) / (WALKER.t1 - WALKER.t0), x = Math.round(W + 14 - u * (W + 28)), y = WALKER.fy + 1;
  const ph = (t - WALKER.t0) * 1.4, s = Math.sin(ph * TAU), l = Math.round(s * 2), bob = Math.abs(s) < 0.35 ? 1 : 0;
  const top = y - 24 - bob, X = x - 7;
  R(X + 5 + l, top + 18, 2, 4, '#1c2230'); R(X + 8 - l, top + 18, 2, 4, '#2a3040');   // trousers
  R(X + 4 + l, top + 22, 3, 2 + bob, '#0e1216'); R(X + 7 - l, top + 22, 3, 2 + bob, '#1a1f26');   // wellies
  if (mir) ctx.drawImage(WALKER_VF, X, 2 * MIR + 2 - top - WALKER_VF.height); else ctx.drawImage(WALKER_C, X, top);
  const fl = Math.round(G * 2.2 + G * wave(t, 300));                  // coat tail flapping downwind
  if (fl > 0) { R(X + 12, top + 14, fl, 1, '#98701a'); R(X + 12, top + 15, fl + 1, 1, '#98701a'); }
}
function drawQuayFolk(t, G) {
  ctx.globalAlpha = 0.2;                                               // their mirror images on the wet stone first
  drawCouple(t, G, true); drawBike(t, G, true); drawWalker(t, G, true);
  ctx.globalAlpha = 1;
  drawCouple(t, G, false); drawBike(t, G, false); drawWalker(t, G, false);
  MIR = -1;
}

/* small far figures on the footbridge (11 px, in profile): out of the shadow at one end, into it at the other */
const BRG_WALK = { x0: 341, x1: 392, fy: BRG.deck - 1 };
function mini(x, fy, c, o = {}) {
  const hy = fy - 10, dir = o.dir || 1, st = o.step;
  R(x, hy, 2, 2, c.skin); R(x, hy, 2, 1, c.hair); R(dir > 0 ? x : x + 1, hy + 1, 1, 1, c.hair);
  R(x, hy + 2, 2, 5, c.coat); R(x, hy + 2, 2, 1, c.lit);
  if (st) { R(x, hy + 7, 2, 1, c.bot); R(x - 1, hy + 8, 1, 3, c.bot); R(x + 2, hy + 8, 1, 3, c.bot); }
  else R(x, hy + 7, 2, 4, c.bot);
  if (o.run) R(x + (st ? dir * 2 : -dir), hy + 4, 1, 1, c.dark);       // arms pumping
  else R(dir > 0 ? x + 1 : x, hy + 3, 1, 3, c.dark);
}
const JOGGER = { skin: '#c89878', hair: '#2a1e18', coat: '#b8d03a', lit: '#eef6a0', dark: '#5a6a20', bot: '#14161c' };
const DOGGER = { skin: '#c8a080', hair: '#6a6060', coat: '#a08664', lit: '#e8cc9a', dark: '#5a4a38', bot: '#1c1c24' };
function drawBridgeFolk(t) {
  const fy = BRG_WALK.fy, fade = x => smooth(BRG_WALK.x0, BRG_WALK.x0 + 6, x) * (1 - smooth(BRG_WALK.x1 - 6, BRG_WALK.x1, x));
  for (const J of JOGS) {
    if (t < J.t0 || t >= J.t0 + JOG_DUR) continue;
    const u = (t - J.t0) / JOG_DUR, x = Math.round(J.dir > 0 ? lerp(336, 397, u) : lerp(397, 336, u)), a = fade(x);
    if (a <= 0) continue;
    ctx.globalAlpha = a;
    mini(x, fy - (Math.floor(t * 6) & 1), JOGGER, { dir: J.dir, step: Math.floor(t * 6) & 1, run: true });
    R(J.dir > 0 ? x + 2 : x - 1, fy - 9, 1, 1, '#ffffff');              // a headlamp
    ctx.globalAlpha = 1;
    halo(HL.white, J.dir > 0 ? x + 2 : x - 1, fy - 9, 0.5 * a);
  }
  if (t >= DOGW.t0 && t < DOGW.t1) {
    const x = Math.round(t < DOGW.stop ? lerp(BRG_WALK.x1 + 3, DOGW.x, (t - DOGW.t0) / (DOGW.stop - DOGW.t0))
      : t < DOGW.go ? DOGW.x : lerp(DOGW.x, BRG_WALK.x0 - 4, (t - DOGW.go) / (DOGW.t1 - DOGW.go)));
    const walking = t < DOGW.stop || t >= DOGW.go, st = walking && (Math.floor(t * 3.2) & 1), a = fade(x);
    if (a > 0) {
      ctx.globalAlpha = a;
      const hy = fy - 10;                                               // under an umbrella
      R(x - 2, hy - 3, 6, 1, '#8a2a34'); R(x - 1, hy - 4, 4, 1, '#c8606a'); R(x + 1, hy - 2, 1, 2, '#1a1418');
      mini(x, fy, DOGGER, { dir: -1, step: st });
      const dx = x - 6, up = !walking && frac(t / 2) < 0.7, dst = walking && (Math.floor(t * 6.4) & 1);   // the dog, on its lead
      R(dx, fy - 2, 4, 2, '#d8ccb0'); R(dx - 1, fy - 3 - (up ? 1 : 0), 2, 2, '#e4dac0'); R(dx, fy - 3 - (up ? 1 : 0), 1, 1, '#8a7a60');
      R(dx + 4, fy - 3 - (!walking && (Math.floor(t * 8) & 1) ? 1 : 0), 1, 1, '#d8ccb0');    // the tail, wagging when it stops
      R(dst ? dx : dx + 1, fy, 1, 1, '#8a7a60'); R(dst ? dx + 3 : dx + 2, fy, 1, 1, '#8a7a60');
      seg(x - 1, hy + 5, dx + 1, fy - 3, '#2a2028');
      ctx.globalAlpha = 1;
    }
  }
}

/* on the Plaza: dark figures against its glow, some standing at the balustrade, some strolling round the building
   (whole circuits a loop, so they leave round one corner and come back round the other) */
const PLAZA_FOLK = [{ x: 190 }, { x: 192 }, { x: 247 }, { k: 2, ph: 0.1 }, { k: -3, ph: 0.45 }, { k: 1, ph: 0.7, pair: true }, { k: -2, ph: 0.9 }];
function drawPlazaFolk(t) {
  const span = EP.x1 - EP.x0 - 2;
  ctx.fillStyle = '#22141a';
  for (const f of PLAZA_FOLK) {
    const u = f.k ? frac(Math.abs(f.k) * t / LOOP + f.ph) : 0, x = f.k ? Math.round(EP.x0 + 1 + (f.k > 0 ? u : 1 - u) * span) : f.x;
    ctx.fillRect(x, PZ.floor - 2, 1, 3);
    if (f.pair) ctx.fillRect(x + 2, PZ.floor - 2, 1, 3);
  }
}

/* ================= Per-frame: wind-driven rain ================= */
function drawRain(L, t, G, lights) {
  const sl = L.s0 + L.s1 * G, len = L.len + Math.round(G * 2), span = W + 30 + L.fall * (L.s0 + L.s1);
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -30 + hash(d.id, cyc, 11) * span;
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12), hash(d.id, cyc, 13));
    if (u < L.ff) {
      const f = u / L.ff, yh = yl - L.fall * (1 - f), xh = xl - (yl - yh) * sl;
      if (yh < -len || xh < -len || xh > W + len) continue;
      const lit = litAt(xh, yh, L.far, lights);
      ctx.fillStyle = lit === 1 ? L.warm : lit === 2 ? L.cool : L.col;
      for (let k = 0; k < len; k++) ctx.fillRect(Math.round(xh - k * sl), Math.round(yh - k), 1, 1);
    } else {
      const s = (u - L.ff) / (1 - L.ff), big = L.len > 6;
      const hgt = Math.sin(Math.PI * s) * (big ? 2.5 : 1.2), spread = 1 + s * (big ? 2.5 : 1.2);
      ctx.globalAlpha = 1 - s;
      ctx.fillStyle = litAt(xl, yl, false, lights) === 1 ? L.warm : L.splash;
      const Y = Math.round(yl - 1 - hgt);
      ctx.fillRect(Math.round(xl - spread + 1), Y, 1, 1); ctx.fillRect(Math.round(xl + spread + 1), Y, 1, 1);
      if (s < 0.35) ctx.fillRect(Math.round(xl), Math.round(yl - 1), 1, 1);
      ctx.globalAlpha = 1;
    }
  }
}
function drawSheet(t, G) {
  const a = 0.2 + 0.6 * G * G, ox = scroll(t, 36, W), oy = scroll(t, 100, H);
  ctx.globalAlpha = a;
  ctx.drawImage(SHEET_C, ox - W, oy - H); ctx.drawImage(SHEET_C, ox, oy - H);
  ctx.drawImage(SHEET_C, ox - W, oy); ctx.drawImage(SHEET_C, ox, oy);
  ctx.globalAlpha = 1;
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the billboard; billboard: false leaves it out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, clock, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  t_ = t; MIR = -1;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const G = gust(t), lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS, wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawFarGulls(t);
  drawFar(t);
  if (wx.rain) drawRain(RAIN[0], t, G, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawElbe(t, G);
  drawShip(t);
  drawElbphi(t);
  drawSpeicherstadt(t, G, pitch, billboard, clock);
  drawBasin(t, G, wx.rain);
  drawBoatPass(FERRY_C, FERRY, t, -1, ferryLights);
  drawBoatPass(BARK_C, BARK, t, 1, barkLights);
  drawMidGulls(t, G);
  if (wx.rain) drawRain(RAIN[1], t, G, lights);
  if (wx.rain) drawSheet(t, G);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawForeground(t, G, wx.rain);
  if (wx.rain) drawRain(RAIN[2], t, G, lights);
  drawNearGulls(t, G);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'hamburg',
  name: 'Hamburg',
  tagline: 'Harbour lights on the Elbe',
  timeZone: 'Europe/Berlin',
  accent: '#4fc3f7',
  focusX: EP_CX,
  landmark: [EP.x0, EP.x1],                            // the Elbphilharmonie
  landmarkTop: 57,                                     // the row of the highest wave's beacon, which landscape crops keep
  billboard: BB,
  perch: { dy: -1 },                                   // the What's new pigeon (site/js/ui/pigeon.js): the frame sits a row lower
  ambience: { rain: 0.7, wind: 0.55, water: 0.35, traffic: 0.1 },
  events: [
    { t: SHIP.t0, type: 'boat', dur: SHIP.t1 - SHIP.t0, pan: [-1, 0.3], gain: 0.45 },
    { t: HORN_T, type: 'horn' },
    { t: GULL_T[0], type: 'gull', pan: 0 },
    { t: BARK.t0, type: 'boat', dur: BARK.t1 - BARK.t0, pan: [-1, 1], gain: 0.6 },
    { t: FERRY.t0, type: 'boat', dur: FERRY.t1 - FERRY.t0, pan: [1, -1], gain: 0.7 },
    { t: GULL_T[1], type: 'gull', pan: 0 },
    ...BELLS.map(t => ({ t, type: 'bell', x: MI.cx, gain: 0.6 })),
    { t: SEAL.t0 + 0.8, type: 'splash', x: SEAL.x, gain: 0.35 },
    { t: SEAL.t1 - 1.1, type: 'splash', x: SEAL.x, gain: 0.7 },
    { t: GULL_T[2], type: 'gull', pan: 0 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
